// Config for every list / create-edit page in the sidebar groups, taken from
// tm-admin-web-react's pages: same table columns, same form fields (labels,
// order, options, required flags) and the same submitted payload keys.
// ResourceListPage / ResourceFormPage render from it, and
// DashboardSidebar/menuItems.js builds the dropdown menus from it.
//
//   slug        -> routes: /dashboard/<slug>-list, /dashboard/create-<slug>/:id
//   endpoint    -> REST path under REACT_APP_API_URL (same as tm)
//   listParams  -> extra query params on list calls (tm: { type: "Income" })
//   submitExtras-> keys merged into every submitted payload (tm: type: "Income")
//   columns     -> list table: { key, label, type, fmt?, value?(row), align?, hidden? }
//                  type: text | number | amount | date | datetime | status | yesno | user | name | join | image
//   details     -> extra columns shown in the expandable row
//   fields      -> form: { name, label, type, required?, placeholder?, options?, optionsFrom?, ... }
//                  type: text | number | email | password | date | datetime-local | textarea | file |
//                        select | status | yesno | relation | multirelation | phonecode | policies | section
//   uploads     -> files sent after save: POST {endpoint}/{path}/:id (field "file")
//   actions     -> row menu labels + extra links/PUTs; readOnly -> list only
//   hidden      -> lookup only (feeds relation dropdowns), no pages/menu entry

import dayjs from "dayjs";
import { SHOP_CATEGORIES } from "../ProductDiscount/shopCategories";
import CartItemsPanel from "../Cart/CartItemsPanel";
import { countries } from "../../utils/country";
import countryStates from "../../utils/countryStates";

const f = (name, label, type = "text", extra = {}) => ({ name, label, type, ...extra });
const col = (key, label, type = "text", extra = {}) => ({ key, label, type, ...extra });
const section = (label, labelOnEdit) => ({ type: "section", label, labelOnEdit, name: `section-${label}` });

// tm converts picked local date-times to ISO strings before sending
const isoDates = (...keys) => (payload) => {
  keys.forEach((key) => {
    if (payload[key]) payload[key] = new Date(payload[key]).toISOString();
  });
  return payload;
};

const STATUS = f("isActive", "Status", "status");
const ACTIVE = col("isActive", "Active", "status");
const CREATED_AT = col("createdDateTime", "Created Date Time", "datetime");
const CREATED_BY = col("createdByUser", "Created By", "user");
const code = (u) => u?.identificationShortCode || "";
const person = (u) => [u?.firstname, u?.lastname].filter(Boolean).join(" ");

// Totals shown above ledger lists (tm: Total Debit / Total Credit / Balance) - sums of the rows on screen
const total = (key) => (rows) => rows.reduce((sum, row) => sum + (+row[key] || 0), 0);
const SUMMARY_DEBIT = { label: "Total Debit", value: total("debit") };
const SUMMARY_CREDIT = { label: "Total Credit", value: total("credit") };
const SUMMARY_BALANCE = { label: "Balance", value: (rows) => total("credit")(rows) - total("debit")(rows) };

// tm's Reject dialog on deposit and credit requests (PUT approve/:id with remarks)
const rejectDialog = (title, alsoHideWhen = () => false) => ({
  label: "Reject Request",
  permission: "approve",
  title,
  path: "approve",
  hideWhen: (row) => row.requestStatus === "REJECTED" || alsoHideWhen(row),
  fields: [{ name: "adminRemarks", label: "Remarks", type: "textarea", required: true, placeholder: "Enter admin remarks" }],
  body: (values) => ({ requestStatus: "REJECTED", adminRemarks: values.adminRemarks }),
  success: "Request rejected",
});

export const USER_TYPES = ["ADMIN", "B2B", "B2C"];
const SERVICES = ["FLIGHT", "VISA", "HOTEL", "TRANSPORT", "RAIL", "TOUR", "UMRAH", "OTHERS"];

// Vendors settle in their own currency: wallet balance + the BDT cost paid for it
// give the ledger's average rate (see services/vendor.js)
const VENDOR_SEED = [
  { vendorName: "Guangzhou Sourcing Co", identificationShortName: 101, vendorType: "Online", vendorCountryCode: "CN", currency: "CNY", availableDepositBalance: 52000, totalBdtCost: 845000, averageRate: 16.25, creditBalance: 0 },
  { vendorName: "Yiwu Trading Ltd", identificationShortName: 102, vendorType: "Offline", vendorCountryCode: "CN", currency: "CNY", availableDepositBalance: 18000, totalBdtCost: 288000, averageRate: 16, creditBalance: 4500 },
  { vendorName: "Hongkong Logistics", identificationShortName: 103, vendorType: "Online", vendorCountryCode: "HK", currency: "USD", availableDepositBalance: 2500, totalBdtCost: 300000, averageRate: 120, creditBalance: 0 },
  { vendorName: "Dhaka Local Supplier", identificationShortName: 104, vendorType: "Offline", vendorCountryCode: "BD", currency: "BDT", availableDepositBalance: 125000, totalBdtCost: 0, averageRate: 0, creditBalance: 12000 },
  { vendorName: "Shenzhen Electronics", identificationShortName: 105, vendorType: "Online", vendorCountryCode: "CN", currency: "CNY", availableDepositBalance: 0, totalBdtCost: 0, averageRate: 16.4, creditBalance: 0 },
].map((vendor, i) => ({
  ...vendor,
  id: i + 1,
  identificationShortCode: `V-${vendor.identificationShortName}`,
  phoneNo: String(1700000100 + i * 31),
  vendorEmail: `vendor${i + 1}@example.com`,
  isAPIprovider: i % 2 === 0,
  isActive: i !== 4,
  ticketIssuePaymentMode: i % 2 ? "CASH" : "CREDIT",
  vendorState: "Guangdong",
  vendorCity: "Guangzhou",
  postalCode: "510000",
  startingDepositBalance: vendor.availableDepositBalance,
  startingBdtCost: vendor.totalBdtCost,
  startingCreditBalance: vendor.creditBalance,
  bankAccounts: [{ bankAccountName: vendor.vendorName, bankAccountNumber: String(2000100 + i * 77), bankName: "Bank of China", bankShortName: "BOC", accountType: "BANK", bankBranch: "Main", routingNumber: "", swiftCode: "" }],
  contactPersons: [{ contactPersonName: "Account Manager", contactPersonDesignation: "Manager", contactPersonDepartment: "Accounts", contactPersonPhoneNo: String(1800000100 + i), contactPersonEmail: `am${i + 1}@example.com` }],
}));

// Sample ledger rows per vendor: a deposit, a purchase payment and a credit entry
const vendorLedgerSeed = () => {
  const rows = [];
  VENDOR_SEED.slice(0, 4).forEach((vendor) => {
    const foreign = vendor.currency !== "BDT";
    const rate = foreign ? vendor.totalBdtCost / vendor.availableDepositBalance : 1;
    let balance = 0;
    let cost = 0;
    [
      ["DEPOSIT", "Deposit", 0, vendor.availableDepositBalance * 1.4, "Wallet deposit"],
      ["PAYMENT", "Offline Purchase", vendor.availableDepositBalance * 0.4, 0, "Offline purchase payment"],
      ["CREDIT_USE", "Credit Use", 0, 0, "Credit purchase"],
    ].forEach(([category, type, debit, credit, narration], n) => {
      balance += credit - debit;
      cost += (credit - debit) * rate;
      rows.push({
        vendorId: vendor.id,
        createdDateTime: dayjs().subtract((3 - n) * 2, "day").toISOString(),
        referenceId: `VL-${vendor.id}${n + 1}0${n + 4}`,
        transactionCategory: category,
        transactionType: type,
        service: "ALL",
        narration,
        debit: Math.round(debit),
        credit: Math.round(credit),
        currentBalance: Math.round(balance),
        creditUsed: n === 2 ? 2000 : 0,
        creditBalance: n === 2 ? 2000 : 0,
        currencyCode: vendor.currency,
        exchangeRate: foreign && credit ? Number(rate.toFixed(4)) : 0,
        bdtAmount: foreign && credit ? Math.round(credit * rate) : 0,
        runningBdtCost: foreign ? Math.round(cost) : 0,
        settlementStatus: n === 2 ? "PENDING" : "N/A",
        isActive: true,
      });
    });
  });
  return rows;
};

export const RESOURCES = {};

const define = (slug, title, endpoint, opts = {}) => {
  const fields = opts.fields || [];
  const editable = !opts.readOnly && fields.length > 0;
  const columns =
    opts.columns ||
    fields
      .filter((x) => x.type !== "textarea" && x.type !== "section" && x.type !== "file")
      .map((x) => col(x.name, x.label, x.type === "status" ? "status" : x.type, { options: x.options }));

  RESOURCES[slug] = {
    pageSizes: [10, 20, 50, 100],
    actions: {},
    details: [],
    uploads: [],
    ...opts,
    slug,
    title,
    endpoint,
    fields,
    columns,
    readOnly: !editable,
    listPath: opts.listPath || `/dashboard/${slug}-list`,
    formPath: editable ? opts.formPath || `/dashboard/create-${slug}` : null,
  };
};

/* ------------------------- lookups (relation dropdowns) ------------------------- */
// Not routed - they feed the autocompletes. Endpoints as in tm's services.

const lookup = (slug, endpoint, label, columns, seed) =>
  define(slug, slug, endpoint, { hidden: true, readOnly: true, optionLabel: label, columns, seed, pageSizes: [100] });

// mock: id 5 is the customer every mock order belongs to (orders carry AG-1002 Sky Travels), so picking it in a
// customer dropdown (e.g. Print Invoices) finds those orders
const AGENT_LOOKUP_SEED = [
  { identificationShortCode: "AG-1005", companyName: "Green Trade" },
  { identificationShortCode: "AG-1006", companyName: "Blue Wave Ltd" },
  { identificationShortCode: "AG-1007", companyName: "Alpha Traders" },
  { identificationShortCode: "AG-1008", companyName: "Delta Corp" },
  { identificationShortCode: "AG-1002", companyName: "Sky Travels" },
];
lookup("agent-lookup", "users/all-agent", (r) => `${code(r)} - ${r.companyName || ""}`,
  [col("identificationShortCode", "Code"), col("companyName", "Company")], AGENT_LOOKUP_SEED);
lookup("customer-lookup", "users/all-customer", (r) => `${code(r)} - ${person(r)}`,
  [col("identificationShortCode", "Code"), col("firstname", "First Name"), col("lastname", "Last Name")]);
lookup("vendor-lookup", "vendors/minimal-data", (r) => `${code(r)} - ${r.vendorName || ""}`,
  [col("identificationShortCode", "Code"), col("vendorName", "Vendor")], VENDOR_SEED);
// Only ACTIVE vendors are offered in any vendor select (orders, purchase dialogs, users, ledgers ...): the API is
// asked for active ones and a deactivated one that still comes back is dropped.
Object.assign(RESOURCES["vendor-lookup"], {
  listParams: { isActive: true },
  clientFilter: (vendor) => vendor.isActive !== false,
});
lookup("user-lookup", "users", (r) => `${code(r)} - ${person(r)}`,
  [col("identificationShortCode", "Code"), col("firstname", "First Name"), col("lastname", "Last Name")]);
lookup("zone-lookup", "zones", (r) => r.name || "", [col("name", "Name")],
  ["Dhaka", "Chattogram", "Sylhet", "Rajshahi", "Khulna"].map((name) => ({ name })));
lookup("responsibility-lookup", "task-responsibility", (r) => r.title || r.name || "", [col("title", "Title")],
  ["Booking Support", "Accounts", "Customer Care", "Sales"].map((title) => ({ title })));

/* --------------------------------- Setting --------------------------------- */

define("setting", "Setting", "setting", {
  columns: [
    col("metakey", "Meta key"),
    col("metavalue", "Meta Value", "text", { openUrl: true }),
    CREATED_AT,
    CREATED_BY,
    ACTIVE,
  ],
  actions: { edit: "Edit Setting", delete: "Delete Setting" },
  fields: [
    f("metakey", "Meta Key", "text", { required: true, placeholder: "Enter Meta Key" }),
    f("metavalue", "Meta Value", "text", { required: true, placeholder: "Enter Meta Value" }),
    STATUS,
    // Only for the "Site Logo" key: tm opens an "Upload Site Logo" dialog after saving
    f("imageFile", "Upload Site Logo", "file", { showIf: (v) => v.metakey === "Site Logo" }),
  ],
  uploads: [{ field: "imageFile", path: "upload-image" }],
  seed: [
    { metakey: "Site Logo", metavalue: "https://cdn.example.com/logo.svg" },
    { metakey: "Site Favicon", metavalue: "https://cdn.example.com/favicon.png" },
    { metakey: "Environment Mode", metavalue: "live" },
    { metakey: "Environment Message", metavalue: "Welcome to Deshmart" },
    { metakey: "Support Email", metavalue: "support@deshmart.com" },
  ].map((row) => ({ ...row, isActive: true })),
});

define("currency", "Currency", "currency", {
  headerLinks: [{ label: "Change Log", to: "/dashboard/currency-change-log" }],
  columns: [
    col("currencyCode", "Currency Code"),
    col("countryCode", "Country Code"),
    col("currencySymbol", "Currency Symbol"),
    col("currencyToBDT", "Currency To BDT", "number"),
    col("bdtToCurrency", "BDT To Currency", "number"),
    col("usdToCurrency", "USD To Currency", "number"),
    col("currencyToUSD", "Currency To USD", "number"),
    ACTIVE,
  ],
  actions: {
    edit: "Edit Currency",
    delete: "Delete Currency",
    extra: [{ label: "Change Log", to: (row) => `/dashboard/currency-change-log/${row.id}` }],
  },
  fields: [
    f("currencyCode", "Currency Code", "text", { required: true, placeholder: "Enter Currency Code" }),
    f("countryCode", "Country Code", "text", { required: true, placeholder: "Enter Country Code" }),
    f("currencySymbol", "Currency Symbol", "text", { placeholder: "Enter Currency Symbol" }),
    f("currencyToBDT", "Currency To BDT", "number", { placeholder: "Enter Currency To BDT" }),
    f("bdtToCurrency", "BDT To Currency", "number", { placeholder: "Enter BDT To Currency" }),
    f("usdToCurrency", "USD To Currency", "number", { placeholder: "Enter USD To Currency" }),
    f("currencyToUSD", "Currency To USD", "number", { placeholder: "Enter Currency To USD" }),
    STATUS,
  ],
  seed: [
    ["USD", "US", "$", 122, 0.0082, 1, 1],
    ["EUR", "EU", "€", 132.4, 0.00755, 0.92, 1.0868],
    ["GBP", "GB", "£", 154.8, 0.00646, 0.79, 1.2658],
    ["INR", "IN", "₹", 1.46, 0.685, 83.5, 0.012],
    ["SAR", "SA", "SAR", 32.5, 0.0308, 3.75, 0.2667],
    ["AED", "AE", "AED", 33.2, 0.0301, 3.67, 0.2725],
    ["MYR", "MY", "RM", 25.9, 0.0386, 4.72, 0.2119],
    ["SGD", "SG", "S$", 90.7, 0.011, 1.35, 0.7407],
  ].map(([currencyCode, countryCode, currencySymbol, currencyToBDT, bdtToCurrency, usdToCurrency, currencyToUSD]) => ({
    currencyCode, countryCode, currencySymbol, currencyToBDT, bdtToCurrency, usdToCurrency, currencyToUSD, isActive: true,
  })),
});

// tm: /dashboard/currency-change-log(/:id) - the :id filters by currencyId
const CURRENCY_FIELDS = [
  ["currencyCode", "Currency Code"],
  ["countryCode", "Country Code"],
  ["currencySymbol", "Currency Symbol"],
  ["currencyToBDT", "Currency To BDT"],
  ["bdtToCurrency", "BDT To Currency"],
  ["usdToCurrency", "USD To Currency"],
  ["currencyToUSD", "Currency To USD"],
  ["isActive", "Active"],
];
const changedFieldLines = (before, after, side) => {
  if (!before || !after) return "-";
  const lines = CURRENCY_FIELDS.filter(([key]) => `${before[key]}` !== `${after[key]}`).map(([key, label]) => {
    const value = (side === "before" ? before : after)[key];
    return `${label}: ${key === "isActive" ? (value ? "Active" : "Inactive") : value ?? "-"}`;
  });
  return lines.length ? lines.join(" | ") : "No field changes";
};
define("currency-change-log", "Currency Change Log", "currency-change-log", {
  readOnly: true,
  listPath: "/dashboard/currency-change-log",
  routeParam: { name: "id", query: "currencyId" },
  dateFilter: "changedDateTime",
  columns: [
    col("currency.currencyCode", "Currency"),
    col("previousData", "Previous Data", "text", { value: (r) => changedFieldLines(r.previousData, r.updatedData, "before") }),
    col("updatedData", "Updated Data", "text", { value: (r) => changedFieldLines(r.previousData, r.updatedData, "after") }),
    col("changedByUser", "Changed By", "name"),
    col("changedDateTime", "Change Date/Time", "datetime"),
    col("currencyId", "Currency Id", "number", { hidden: true }),
  ],
});

define("banner", "Banner", "banners", {
  columns: [
    col("type", "Banner Type"),
    col("title", "Title"),
    col("userType", "User Type"),
    col("link", "Link"),
    col("serviceType", "Service Type"),
    col("location", "Location"),
    col("imgUrl", "Image", "link", { linkLabel: "Banner Image" }),
    ACTIVE,
  ],
  actions: { edit: "Edit Banner", delete: "Delete Banner" },
  fields: [
    f("type", "Banner Type", "select", { required: true, options: ["Banner", "PopUp"], placeholder: "Select Banner Type" }),
    f("title", "Title", "text", { required: true, placeholder: "Enter Title" }),
    f("userType", "User Type", "select", { options: USER_TYPES, placeholder: "Select User Type" }),
    f("link", "Link", "text", { placeholder: "Enter Link" }),
    f("serviceType", "Service Type", "select", {
      options: ["FLIGHT", "VISA", "PACKAGE", "TOUR", "INSURANCE", "MULTIPLE", "WALLET"],
      placeholder: "Select Banner Service Type",
    }),
    f("location", "Location", "select", {
      options: ["Dashboard", "SignIn", "SignUp", "Deposit Request", "Booking Details", "Public"],
      placeholder: "Select Banner Location",
    }),
    STATUS,
    f("imageFile", "Image", "file", { hint: "(Please upload a document no larger than 1 MB)" }),
  ],
  uploads: [{ field: "imageFile", path: "upload-image" }],
});

define("notice", "Notice", "notices", {
  columns: [
    col("title", "Title"),
    col("description", "Description"),
    col("userType", "User Type"),
    col("location", "Location"),
    col("service", "Service"),
    col("createdDateTime", "Created Date", "datetime"),
    CREATED_BY,
    ACTIVE,
  ],
  actions: { edit: "Edit Notice", delete: "Delete Notice" },
  fields: [
    f("title", "Title", "text", { required: true, placeholder: "Enter Title" }),
    f("description", "Description", "textarea", { placeholder: "Enter Description" }),
    f("userType", "User Type", "select", { options: USER_TYPES, placeholder: "Select User Type" }),
    f("location", "Location", "select", {
      required: true,
      options: ["Dashboard", "Credit Request", "Deposit Request", "Public"],
      placeholder: "Select Banner Location",
    }),
    f("service", "Service", "select", { options: SERVICES, placeholder: "Select Service Type" }),
    STATUS,
  ],
});

/* --------------------------------- Activity --------------------------------- */

define("user-activity", "User Activity", "user-activity", {
  readOnly: true,
  dateFilter: "createdDateTime",
  columns: [
    col("createdDateTime", "Date Time", "datetime", { fmt: "DD-MM-YYYY hh:mm A" }),
    col("user", "User", "user"),
    col("activityType", "Activity Type", "text", { options: ["Create", "Update", "Delete", "Login", "Export"] }),
    col("activityText", "Activity Text"),
    col("foreignTableName", "Foreign Table", "text", { options: ["orders", "currency", "banners", "users"] }),
    col("foreignTablePrimaryId", "Foreign Table ID", "number"),
  ],
});

define("login-device", "Login Device", "user-loggedin-device", {
  readOnly: true,
  columns: [
    col("lastLoginDateTime", "Last Login Time", "datetime", { fmt: "DD-MM-YYYY hh:mm A" }),
    col("user", "User", "user"),
    col("deviceName", "Device Name", "text", { options: ["Chrome on Windows", "Safari on iPhone", "Edge on Windows", "Firefox on Linux"] }),
    col("deviceOS", "Device OS"),
    col("browser", "Browser"),
    col("userIp", "IP Address"),
    col("deviceIdentificationId", "Device ID"),
  ],
  actions: {
    extra: [{ label: "View Login Activity", to: (row) => `/dashboard/login-activity-list?userLoggedinDeviceId=${row.id}&userId=${row.user?.id ?? ""}` }],
  },
});

define("login-activity", "Login Activity", "user-loggedin-activity", {
  readOnly: true,
  dateFilter: "createdDateTime",
  columns: [
    col("createdDateTime", "Date Time", "datetime", { fmt: "DD-MM-YYYY hh:mm A" }),
    col("activityType", "Activity Type", "text", { options: ["Login", "Logout", "Login", "Failed Login"] }),
    col("user", "User", "user"),
    col("userLoggedinDevice.deviceName", "Device Name"),
    col("userLoggedinDevice.deviceOS", "Device OS"),
    col("userLoggedinDevice.browser", "Browser"),
    col("userLoggedinDevice.userIp", "IP Address"),
  ],
});

/* --------------------------------- Employee --------------------------------- */

define("employee", "Employee", "employees", {
  saveMessage: "",
  columns: [
    col("identificationShortCode", "ID"),
    col("user", "Name", "name", { value: (r) => person(r.user) || "N/A" }),
    col("user.phoneNo", "Phone No.", "text", { value: (r) => [r.user?.phoneCode, r.user?.phoneNo].filter(Boolean).join("") || "N/A" }),
    col("user.email", "Email"),
    col("user.identificationShortCode", "System User Id"),
    col("employmentType", "Employment Type", "text", { options: ["Full-time", "Part-time", "Internship", "Contractual"] }),
    col("dateOfJoining", "Joining Date", "date", { fmt: "DD/MM/YYYY" }),
    col("user.department.title", "Department", "text", { options: ["Sales", "Operations", "Accounts", "IT", "HR"] }),
    col("designation.title", "Designation", "text", { options: ["Manager", "Executive", "Officer", "Assistant"] }),
    col("status", "Employee Status", "text", { options: ["Active", "Active", "On Leave", "Suspended"] }),
    col("user.isActive", "User Status", "status"),
  ],
  details: [
    col("createdByUser", "Created By", "name"),
    CREATED_AT,
    col("updatedByUser", "Updated By", "name"),
    col("updatedDateTime", "Updated Date Time", "datetime"),
    col("employeePhoto", "Photo", "link", { linkLabel: "View Photo" }),
    col("employeeNID", "NID", "link", { linkLabel: "View NID" }),
  ],
  actions: { edit: "Edit", delete: "Delete" },
  fields: [
    section("User Create Credentials", "User Update Credentials"),
    f("firstname", "First Name", "text", { required: true, placeholder: "Enter First Name", nested: "user" }),
    f("lastname", "Last Name", "text", { required: true, placeholder: "Enter Last Name", nested: "user" }),
    f("email", "Email", "email", { required: true, placeholder: "Enter Email", nested: "user" }),
    f("password", "Password", "password", { placeholder: "Enter Password", requiredOnCreate: true, labelOnEdit: "Change Password" }),
    f("phoneCode", "Phone Code", "phonecode", { required: true, nested: "user" }),
    f("phoneNo", "Phone No", "text", { required: true, placeholder: "Enter Phone No Without 0", nested: "user" }),
    f("address", "Present Address", "text", { placeholder: "Enter Present Address", nested: "user" }),
    f("city", "City", "text", { placeholder: "Enter City", nested: "user" }),
    f("state", "State", "text", { placeholder: "Enter State", nested: "user" }),
    f("countryCode", "Country Code", "text", { placeholder: "Enter Country Code", nested: "user" }),
    f("defaultCurrency", "Default Currency", "text", { placeholder: "Enter Default Currency", nested: "user" }),
    f("isActive", "User Status", "status", { nested: "user" }),
    f("roleIds", "Roles", "multirelation", { optionsFrom: "role", placeholder: "Select Roles", prefill: (e) => e.user?.roles?.map((r) => Number(r.id)) }),
    f("responsibilityIds", "Responsibilities", "multirelation", { optionsFrom: "responsibility-lookup", placeholder: "Select Responsibilities", prefill: (e) => e.user?.responsibilities?.map((r) => Number(r.id)) }),
    // default vendor of this user: pre-selected in Purchase Initiate / Purchase Complete (optional)
    f("vendorId", "Vendor", "relation", { optionsFrom: "vendor-lookup", placeholder: "Select Vendor", prefill: (e) => e.user?.vendorId ?? e.user?.vendor?.id ?? "" }),
    f("isOnline", "Online Status", "select", { options: [{ value: true, label: "Online" }, { value: false, label: "Offline" }] }),
    section("Employee Details"),
    f("dateOfBirth", "Date Of Birth", "date", { placeholder: "DD/MM/YYYY" }),
    f("nationality", "Nationality", "select", { options: ["Bangladeshi"], placeholder: "Select Nationality" }),
    f("personalEmail", "Personal Email", "email", { placeholder: "Enter Personal Email" }),
    f("personalPhoneNumber", "Personal Phone No.", "text", { placeholder: "Enter Personal Phone No." }),
    f("permanentAddress", "Permanent Address", "text", { placeholder: "Enter Permanent Address" }),
    f("gender", "Gender", "select", { options: ["Male", "Female", "Other"], placeholder: "Select Gender" }),
    f("religion", "Religion", "select", { options: ["Islam", "Hinduism", "Buddhism", "Christianity", "Other"], placeholder: "Select Religion" }),
    f("maritalStatus", "Marital Status", "select", { options: ["Unmarried", "Married", "Divorce", "Widow", "Widower"], placeholder: "Select Marital Status" }),
    f("employmentType", "Employment Type", "select", { options: ["Full-time", "Part-time", "Internship", "Contractual"], placeholder: "Select Employment Type" }),
    f("emergencyContactName", "Emg. Contact Name", "text", { placeholder: "Enter Emg. Contact Name" }),
    f("emergencyContactNumber", "Emg. Contact No.", "text", { placeholder: "Enter Emg. Contact No." }),
    f("emergencyContactRelationship", "Emg. Contact Relationship", "text", { placeholder: "Enter Emg. Contact Relationship" }),
    f("dateOfJoining", "Date Of Joining", "date", { placeholder: "DD/MM/YYYY" }),
    f("salary", "Salary", "number", { placeholder: "Enter Salary" }),
    f("bankAccountNumber", "Bank Account Number", "text", { placeholder: "Enter Bank Account Number" }),
    f("bankName", "Bank Name", "text", { placeholder: "Enter Bank Name" }),
    f("taxIdentificationNumber", "Tax Identification No", "text", { placeholder: "Enter Tax Identification No" }),
    f("shiftTiming", "Shift Timing", "text", { placeholder: "Enter Shift Timing" }),
    f("workDays", "Work Days", "text", { placeholder: "Enter Work Days" }),
    f("status", "Status", "select", { options: ["Active", "Inactive", "On Leave", "Suspended", "Resigned", "Retired"], placeholder: "Select Status" }),
    f("departmentId", "Department", "relation", { required: true, optionsFrom: "department", placeholder: "Select Department" }),
    f("designationId", "Designation", "relation", { required: true, optionsFrom: "designation", placeholder: "Select Designation" }),
    f("supervisorId", "Supervisor", "relation", { optionsFrom: "employee", placeholder: "Select Supervisor" }),
    f("isKam", "Is KAM", "yesno"),
    f("isCam", "Is CAM", "yesno"),
    f("isCAMode", "Is CA Mode", "yesno"),
    f("photoFile", "Photo", "file"),
    f("nidFile", "NID", "file"),
  ],
  // no vendor picked = no link (null clears an existing one)
  prepare: (payload) => ({ ...payload, vendorId: payload.vendorId === "" || payload.vendorId === undefined ? null : payload.vendorId }),
  uploads: [
    { field: "photoFile", path: "upload-photo" },
    { field: "nidFile", path: "upload-nid" },
  ],
  optionLabel: (r) => `${r.identificationShortCode || ""} - ${person(r.user)}`,
});

define("department", "Department", "departments", {
  columns: [col("title", "Title"), ACTIVE],
  actions: { edit: "Edit Department", delete: "Delete Department" },
  fields: [f("title", "Title", "text", { required: true, placeholder: "Enter Title" }), STATUS],
  optionLabel: (r) => r.title,
  seed: ["Sales", "Operations", "Accounts", "IT", "HR"].map((title) => ({ title, isActive: true })),
});

define("designation", "Designation", "designations", {
  columns: [col("title", "Title"), col("supervisorDesignation.title", "Superviser Designation"), ACTIVE],
  actions: { edit: "Edit Designation", delete: "Delete Designation" },
  fields: [
    f("title", "Title", "text", { required: true, placeholder: "Enter Title" }),
    f("supervisorDesignationId", "Supervisor Designation", "relation", { optionsFrom: "designation", placeholder: "Select Supervisor Designation" }),
    STATUS,
  ],
  optionLabel: (r) => r.title,
  seed: ["Manager", "Executive", "Officer", "Assistant"].map((title, i) => ({
    title,
    supervisorDesignation: { title: i ? "Manager" : "Director" },
    isActive: true,
  })),
});

define("role", "Role", "roles", {
  tabs: { param: "userType", items: USER_TYPES.map((value) => ({ label: value, value })) },
  saveMessage: "Role",
  columns: [
    col("name", "Name"),
    col("userType", "User Type", "text", { value: (r) => (r.userType ? r.userType[0].toUpperCase() + r.userType.slice(1).toLowerCase() : ""), options: USER_TYPES }),
    col("permissions", "Permissions", "text", { value: (r) => (r.fullAccess ? "Full access" : r.permissions?.length ? `${r.permissions.length} permissions` : "-") }),
    ACTIVE,
  ],
  actions: { edit: "Edit Role", delete: "Delete Role" },
  fields: [
    f("name", "Name", "text", { required: true, placeholder: "Enter Name" }),
    f("userType", "User Type", "select", {
      required: true,
      options: [{ value: "ADMIN", label: "Admin" }, "B2B", "B2C"],
      placeholder: "Select User Type",
    }),
    STATUS,
    // what the role may do in the admin panel (the permissions apply to the staff of the panel: admins and employees)
    f("permissions", "Permissions", "permissions", { prefill: (e) => (e.fullAccess ? ["*"] : e.permissions || []) }),
  ],
  optionLabel: (r) => r.name,
  seed: ["Super Admin", "Accounts", "Support", "Sales"].map((name, i) => ({
    name,
    userType: i === 0 ? "ADMIN" : USER_TYPES[i % 3],
    isActive: true,
    fullAccess: i === 0,
    permissions: i === 1 ? ["dashboard.view", "customer.view", "deposit-request.view", "deposit-request.approve"] : [],
  })),
});

define("user-task", "User Task", "user-tasks", {
  pageSizes: [20, 50, 100, 200, 500],
  saveMessage: "User Task",
  columns: [
    col("taskTitle", "Title"),
    col("taskDescription", "Description"),
    col("assignedToUser", "Assigned To", "user"),
    col("taskStartDateTime", "Task Start Date", "datetime"),
    col("taskEndDateTime", "Task End Date", "datetime"),
    col("taskScore", "Task Score", "number"),
    col("priorityStatus", "Task Priority", "text", { options: ["High", "Very High", "Medium", "Low", "Very Low"] }),
    col("taskStatus", "Task Status", "text", { options: ["Not Assigned", "Assigned", "Reassigned", "Pending", "Started", "Completed"] }),
  ],
  details: [col("createdByUser", "Created By", "name"), col("createdDateTime", "Created Date", "datetime"), col("updatedDateTime", "Updated Date", "datetime")],
  actions: { edit: "Edit Task", delete: "Delete Task" },
  fields: [
    f("taskTitle", "Title", "text", { required: true, placeholder: "Enter Title" }),
    f("departmentId", "Department", "relation", { required: true, optionsFrom: "department", placeholder: "Select Department" }),
    f("assignedToUserId", "Assigned To User", "relation", { required: true, optionsFrom: "user-lookup", placeholder: "Select Assigned To User" }),
    f("assignedByUserId", "Assigned By User", "relation", { optionsFrom: "user-lookup", placeholder: "Select Assigned By User" }),
    f("taskStartDateTime", "Task Start Date", "datetime-local", { placeholder: "DD/MM/YYYY HH:MM" }),
    f("taskEndDateTime", "Task End Date", "datetime-local", { placeholder: "DD/MM/YYYY HH:MM" }),
    f("taskScore", "Task Score", "number", { placeholder: "Enter Task Score" }),
    f("priorityStatus", "Priority Status", "select", { options: ["High", "Very High", "Medium", "Low", "Very Low"], placeholder: "Select Priority Status" }),
    f("taskStatus", "Task Status", "select", { options: ["Not Assigned", "Assigned", "Reassigned", "Pending", "Started", "Completed"], placeholder: "Select Task Status" }),
    f("taskDescription", "Description", "textarea", { required: true, placeholder: "Enter Description" }),
  ],
  prepare: isoDates("taskStartDateTime", "taskEndDateTime"),
});

/* --------------------------------- Finance --------------------------------- */

define("cash-receipt", "Cash Receipt", "cash-receipts", {
  saveMessage: "Cash Receipt",
  columns: [
    col("refNo", "Ref No"),
    col("userType", "User Type", "text", { options: ["B2B", "B2C"] }),
    col("b2bUser", "Agent", "text", { value: (r) => [code(r.b2bUser), r.b2bUser?.companyName].filter(Boolean).join(" - ") || "N/A" }),
    col("receivedFrom", "Received From"),
    col("date", "Date", "date"),
    col("amount", "Amount", "amount", { value: (r) => `${r.currency || "BDT"} ${r.amount ?? ""}`.trim() }),
    col("bankAccount.bankName", "Company Bank A/c"),
    col("receivedBy", "Received By"),
    col("paymentMethod", "Payment Method", "text", { options: ["CASH", "CHEQUE", "OTHER"] }),
    col("status", "Status", "text", { options: ["Approved", "Pending", "Approved"] }),
  ],
  details: [col("remarks", "Remarks"), col("createdByUser", "Created By", "name"), col("createdDateTime", "Created Date", "date", { fmt: "DD-MMM-YY" })],
  actions: {
    edit: "Edit",
    delete: "Delete",
    upload: [{ label: "Upload Attachment", path: "upload-attachment" }],
    print: [
      {
        label: "Print Receipt",
        title: "Cash Receipt",
        pairs: (row) => [
          ["Ref No", row.refNo || "-"],
          ["Date", row.date ? String(row.date).slice(0, 10) : "-"],
          ["Received From", row.receivedFrom || "-"],
          ["Agent", [code(row.b2bUser), row.b2bUser?.companyName].filter(Boolean).join(" - ") || "-"],
          ["Amount", `${row.currency || "BDT"} ${row.amount ?? ""}`.trim()],
          ["Company Bank A/c", row.bankAccount?.bankName || "-"],
          ["Received By", row.receivedBy || "-"],
          ["Payment Method", row.paymentMethod || "-"],
          ["Remarks", row.remarks || "-"],
        ],
      },
    ],
  },
  formPath: "/dashboard/finance/create-cash-receipt",
  fields: [
    f("userType", "User Type", "select", { required: true, options: ["B2B", "B2C"], default: "B2B" }),
    f("b2bUserId", "Agent", "relation", { required: true, optionsFrom: "agent-lookup", placeholder: "Search Agent", showIf: (v) => (v.userType || "B2B") === "B2B" }),
    f("userId", "Customer", "relation", { required: true, optionsFrom: "customer-lookup", placeholder: "Search Customer", showIf: (v) => v.userType === "B2C" }),
    f("receivedFrom", "Received From", "text", { placeholder: "Enter Received From" }),
    f("date", "Date", "date", { required: true, placeholder: "Enter Date of Birth" }),
    f("amount", "Amount", "number", { required: true, placeholder: "Enter Amount" }),
    f("bankAccountId", "Company Bank A/c", "relation", { required: true, optionsFrom: "bank-account", placeholder: "Search Bank A/c" }),
    f("receivedBy", "Received By", "text", { placeholder: "Enter Received By" }),
    f("paymentMethod", "Payment Method", "select", { required: true, options: ["CASH", "CHEQUE", "OTHER"], placeholder: "Select Method" }),
    f("remarks", "Remarks", "textarea", { placeholder: "Enter Remarks" }),
  ],
  uploads: [],
});

// The deposit request keeps the amount, the gateway charge and its type (Percentage / Fixed). Like the backend when it
// approves the request: charge = floor(amount x % / 100) for a percentage, else the fixed charge;
// the deposited amount (what reaches the wallet) = amount - charge.
const depositFee = (r) => {
  const amount = +r.depositAmount || 0;
  const charge = +r.depositCharge || 0;
  return r.depositChargeType === "Percentage" ? Math.floor((amount * charge) / 100) : charge;
};
const money2 = (n) => (+n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const depositFeeText = (r) => {
  if (r.depositCharge === null || r.depositCharge === undefined || r.depositCharge === "") return "-";
  return r.depositChargeType === "Percentage" ? `${+r.depositCharge}%` : money2(r.depositCharge);
};
const depositedAmount = (r) => Math.max(0, (+r.depositAmount || 0) - depositFee(r));

define("deposit-request", "Deposit Request List", "user-deposit-request", {
  addLabel: "Add Deposit Request",
  dateFilter: "depositDate",
  tabs: { param: "requestStatus", items: [{ label: "Pending", value: "PENDING" }, { label: "Approved", value: "ACC_APPROVED" }, { label: "Rejected", value: "REJECTED" }] },
  columns: [
    col("depositReference", "Deposit ID"),
    col("depositDate", "Date", "date", { fmt: "DD/MM/YYYY" }),
    col("depositAmount", "Amount", "amount"),
    // the charge as the API keeps it: a percentage or a fixed amount (mock: 1.5% on odd rows, 50 fixed on even ones)
    col("depositChargeType", "Charge Type", "text", { hidden: true, mock: (i) => (i % 2 ? "Percentage" : "Fixed") }),
    col("depositCharge", "Gateway Fee", "text", { value: depositFeeText, mock: (i) => (i % 2 ? 1.5 : 50) }),
    col("totalAmount", "Deposited Amount", "amount", { value: depositedAmount }),
    col("paymentType", "Deposit Type", "text", { options: ["BANK TRANSFER", "BANK DEPOSIT", "CHEQUE"] }),
    col("fromBankName", "From Bank", "text", { options: ["Islami Bank", "City Bank", "BRAC Bank"] }),
    col("b2bUser", "Agent Name", "text", { value: (r) => [code(r.b2bUser), r.b2bUser?.companyName].filter(Boolean).join(" - ") || "N/A" }),
    col("toBankName", "To Bank", "text", { options: ["Dutch-Bangla Bank", "Islami Bank"] }),
    col("transactionReference", "Transaction Refference"),
    col("requestStatus", "Status", "text", { options: ["PENDING", "ACC_APPROVED", "REJECTED"], value: (r) => (r.requestStatus === "ACC_APPROVED" ? "APPROVED" : r.requestStatus) }),
  ],
  details: [col("createdByUser", "Created By", "name"), CREATED_AT, col("depositPaySlipUrl", "Pay Slip", "link", { linkLabel: "View Deposit Pay Slip" })],
  actions: {
    edit: "Edit",
    delete: "Delete Request",
    // an approved request is final: no Reject Request, Edit or Delete Request on it
    hideEditWhen: (row) => row.requestStatus === "ACC_APPROVED",
    hideDeleteWhen: (row) => row.requestStatus === "ACC_APPROVED",
    // a rejected request has no actions at all
    noActionsWhen: (row) => row.requestStatus === "REJECTED",
    download: [{ label: "Download", keys: ["depositPaySlipUrl", "payslipUrl", "payslip", "attachmentUrl"] }],
    dialogs: [
      rejectDialog("Reject Deposit Request", (row) => row.requestStatus === "ACC_APPROVED"),
      {
        label: "Approve Request",
        permission: "approve",
        title: "Approve Deposit Request",
        path: "approve",
        hideWhen: (row) => row.requestStatus === "ACC_APPROVED",
        fields: [
          f("transactionReference", "Transaction Reference", "text", { required: true, placeholder: "Enter transaction reference" }),
          f("toBankAccountId", "Bank A/c", "relation", { required: true, optionsFrom: "bank-account", placeholder: "Select a bank" }),
          f("approvedDateTime", "Approved Date Time", "datetime-local"),
          f("depositDate", "Deposit Date", "date"),
          f("adminRemarks", "Admin Remarks", "textarea", { required: true, placeholder: "Enter admin remarks" }),
          f("clientRemarks", "Client Remarks", "textarea", { placeholder: "Enter client remarks (optional)" }),
        ],
        initial: (row) => ({
          transactionReference: row.transactionReference || "",
          toBankAccountId: row.toBankAccountId ?? "",
          // today's date and time, as a datetime-local value (local time, no seconds)
          approvedDateTime: dayjs().format("YYYY-MM-DDTHH:mm"),
          depositDate: String(row.depositDate || "").slice(0, 10),
        }),
        body: (values, row) => ({
          id: row.id,
          requestStatus: "ACC_APPROVED",
          transactionReference: values.transactionReference,
          adminRemarks: values.adminRemarks,
          clientRemarks: values.clientRemarks,
          toBankAccountId: values.toBankAccountId ? +values.toBankAccountId : null,
          approvedDateTime: values.approvedDateTime ? new Date(values.approvedDateTime).toISOString() : new Date().toISOString(),
          depositDate: values.depositDate || row.depositDate,
        }),
        success: "Request approved and updated with bank info.",
      },
      {
        label: "Change Bank Account",
        title: "Change Bank Account",
        path: "change-bank",
        hideWhen: (row) => row.requestStatus !== "ACC_APPROVED",
        fields: [f("toBankAccountId", "Bank A/c", "relation", { required: true, optionsFrom: "bank-account", placeholder: "Search Bank A/c" })],
        body: (values) => ({ toBankAccountId: +values.toBankAccountId }),
        success: "Bank account changed",
      },
    ],
  },
  formPath: "/dashboard/create-deposit-request",
  fields: [
    f("b2bUserId", "Agent", "relation", { required: true, optionsFrom: "agent-lookup", placeholder: "Search Agent" }),
    f("paymentType", "Deposit Type", "select", { required: true, options: ["BANK TRANSFER", "BANK DEPOSIT", "CHEQUE"], placeholder: "Select Deposit Type" }),
    f("fromBankAccountNumber", "From Account", "text", { required: true, placeholder: "Account Number" }),
    f("depositDate", "Deposit Date", "date", { required: true }),
    f("depositAmount", "Deposit Amount", "number", { required: true, placeholder: "Enter Amount" }),
    f("transactionReference", "Reference ID", "text", { required: true, placeholder: "Enter Reference ID" }),
    f("photoFile", "Attachment", "file", { required: true }),
    f("userNotes", "Notes", "textarea", { placeholder: "Enter Notes" }),
    f("toBankAccountId", "Deposit to", "relation", { optionsFrom: "bank-account", placeholder: "Select a bank" }),
  ],
  uploads: [{ field: "photoFile", path: "upload-payslip" }],
  successMessage: "Request Submitted successfully",
});

define("credit-request", "Credit Request List", "user-credit-request", {
  readOnly: true,
  tabs: { param: "requestStatus", items: [{ label: "Pending", value: "PENDING" }, { label: "KAM Approved", value: "KAM_APPROVED" }, { label: "Rejected", value: "REJECTED" }] },
  columns: [
    col("createdDateTime", "Date", "date", { fmt: "DD-MMM-YYYY" }),
    col("amount", "Amount", "amount"),
    col("dueDateTime", "Due Date", "date", { fmt: "DD-MMM-YYYY" }),
    col("b2bUser", "Agent Name", "text", { value: (r) => [code(r.b2bUser), r.b2bUser?.companyName].filter(Boolean).join(" - ") || "N/A" }),
    col("settleAmount", "Settle Amount", "amount"),
    col("paymentStatus", "Payment Status", "text", { options: ["Paid", "Unpaid", "Partially Paid"] }),
    col("requestStatus", "Request Status", "text", { options: ["Pending", "Kam-Approved", "Rejected"] }),
  ],
  details: [col("createdByUser", "Created By", "name"), CREATED_AT],
  actions: {
    delete: "Delete Request",
    dialogs: [
      rejectDialog("Reject Credit Request"),
      {
        label: "Approve Request",
        permission: "approve",
        title: "Approve Credit Request",
        path: "approve",
        hideWhen: (row) => row.requestStatus === "ACC_APPROVED",
        fields: [
          f("adminRemarks", "Admin Remarks", "textarea", { required: true, placeholder: "Enter admin remarks" }),
          f("clientRemarks", "Client Remarks", "textarea", { placeholder: "Enter client remarks (optional)" }),
        ],
        body: (values) => ({ requestStatus: "ACC_APPROVED", adminRemarks: values.adminRemarks, clientRemarks: values.clientRemarks }),
        success: "Request approved",
      },
    ],
  },
});

define("asset", "Assets", "assets", {
  addLabel: "Add Asset",
  formPath: "/dashboard/assets/create",
  saveMessage: "Asset",
  columns: [
    col("assetCode", "Asset Code"),
    col("assetName", "Asset Name"),
    col("assetType", "Type", "text", { options: ["Tangible", "Intangible", "Current", "Fixed"] }),
    col("purchaseValue", "Purchase Value", "amount"),
    col("currentValue", "Current Value", "amount"),
    col("purchaseDate", "Purchase Date", "date", { fmt: "DD-MMM-YY" }),
    col("usefulLifeMonths", "Useful Life (Months)", "number"),
    col("salvageValue", "Salvage Value", "amount"),
    col("createdByUser.identificationShortCode", "Created By"),
    col("description", "Remarks"),
    ACTIVE,
  ],
  actions: { edit: "Edit", delete: "Delete" },
  fields: [
    f("assetName", "Asset Name", "text", { required: true, placeholder: "Enter Asset Name" }),
    f("assetCode", "Asset Code", "text", { required: true, placeholder: "Enter Asset Code" }),
    f("assetType", "Asset Type", "select", { required: true, options: ["Tangible", "Intangible", "Current", "Fixed"], placeholder: "Select Asset Type" }),
    f("purchaseValue", "Purchase Value", "number", { required: true, placeholder: "Enter Purchase Value" }),
    f("currentValue", "Current Value", "number", { placeholder: "Enter Current Value" }),
    f("purchaseDate", "Purchase Date", "date"),
    f("usefulLifeMonths", "Useful Life (Months)", "number", { placeholder: "Enter Useful Life in Months" }),
    f("salvageValue", "Salvage Value", "number", { placeholder: "Enter Salvage Value" }),
    f("description", "Description", "textarea", { placeholder: "Enter Description" }),
    STATUS,
  ],
});

/* --------------------------------- Voucher --------------------------------- */

define("ledger-party", "Ledger Party", "account-parties", {
  actionIcons: [{ label: "Transaction Ledger", to: (row) => `/dashboard/voucher-ledger-list?accountPartyId=${row.id}` }],
  addLabel: "Add Party",
  formPath: "/dashboard/transactions/create-account-party",
  saveMessage: "Party",
  columns: [
    col("identificationShortCode", "Party ID"),
    col("partyType", "Party Type", "text", { options: ["Person", "Company"] }),
    col("partyName", "Party Name"),
    col("partyPhoneNo", "Phone No", "text", { value: (r) => [r.partyPhoneCode, r.partyPhoneNo].filter(Boolean).join("") }),
    col("partyEmail", "Email"),
    col("partyAddress", "Address"),
    col("description", "Remarks"),
    ACTIVE,
  ],
  actions: { edit: "Edit", delete: "Delete" },
  fields: [
    f("partyType", "Party Type", "select", { required: true, options: ["Person", "Company"], placeholder: "Select Party Type" }),
    f("partyName", "Party Name", "text", { required: true, placeholder: "Enter Party Name" }),
    f("partyPhoneCode", "Phone Code", "phonecode"),
    f("partyPhoneNo", "Phone No", "text", { required: true, placeholder: "Enter Phone No Without 0" }),
    f("partyEmail", "Email", "email", { placeholder: "Enter Email" }),
    f("partyAddress", "Address", "text", { placeholder: "Enter Address" }),
    f("description", "Remarks", "text", { placeholder: "Enter Remarks" }),
    STATUS,
  ],
  optionLabel: (r) => `${r.identificationShortCode || ""} - ${r.partyName || ""}`,
});

const head = (slug, title, type, label, formPath, names) =>
  define(slug, title, "account-heads", {
    formPath,
    listParams: { type },
    submitExtras: { type },
    columns: [col("name", "Head Name", "text", { options: names }), col("description", "Description"), ACTIVE],
    actions: { edit: "Edit", delete: "Delete" },
    fields: [
      f("name", "Head Name", "text", { required: true, placeholder: "Enter Head Name" }),
      f("description", "Description", "text", { placeholder: "Enter Description" }),
      STATUS,
    ],
    optionLabel: (r) => r.name,
    saveMessage: `${label} Head`,
    addLabel: `Add ${label} Head`,
    actionIcons: [{ label: `${label} Voucher List`, to: (row) => `/dashboard/${type === "Income" ? "receipt" : "payment"}-voucher-list?accountHeadId=${row.id}` }],
  });
head("receipt-head", "Receipt Heads", "Income", "Receipt", "/dashboard/transactions/create-receipt-head", ["Ticket Sale", "Service Charge", "Commission", "Other Income"]);
head("payment-head", "Payment Heads", "Expense", "Payment", "/dashboard/transactions/create-payment-head", ["Salary", "Rent", "Utilities", "Marketing", "Vendor Payment"]);

// Receipt / payment voucher = ledger rows of that type; the form is the voucher entry
const LEDGER_COLUMNS = (balance) => [
  col("createdDateTime", "Transation Date", "datetime"),
  col("referenceId", "Reference ID"),
  col("transactionType", "Type", "text", { options: ["Income", "Expense"] }),
  col("narration", "Narration"),
  col("debit", "Debit [DR]", "amount"),
  col("credit", "Credit [CR]", "amount"),
  ...(balance ? [col(balance, "Balance", "amount")] : []),
];
const VOUCHER_FIELDS = (headLabel, headSlug) => [
  f("createdDateTime", "Transaction Date", "datetime-local", { required: true, placeholder: "Enter Created Date Time" }),
  f("accountPartyId", "Ledger Party", "relation", { required: true, optionsFrom: "ledger-party", placeholder: "Search Ledger Party" }),
  f("accountHeadId", headLabel, "relation", { required: true, optionsFrom: headSlug, placeholder: `Search ${headLabel}` }),
  f("companyBankAccountId", "Company Bank A/c", "relation", { required: true, optionsFrom: "bank-account", placeholder: "Search Bank A/c" }),
  f("bankTransId", "Transation Reference", "text", { required: true, placeholder: "Enter Transation Reference" }),
  f("amount", "Amount", "number", { required: true, placeholder: "Enter Amount" }),
  f("remarks", "Remarks", "textarea", { placeholder: "Enter Remarks" }),
  f("transactionReceipt", "Receipt", "file", { hint: '(No larger than "1 MB")' }),
];

define("receipt-voucher", "Receipt Voucher", "account-transactions", {
  ledger: { uploadPath: "upload-scan-copy" },
  summary: [SUMMARY_DEBIT, SUMMARY_CREDIT, SUMMARY_BALANCE],
  formPath: "/dashboard/transactions/create-receipt",
  listParams: { transactionType: "Income" },
  submitExtras: { transactionType: "Income" },
  pageSizes: [20, 50, 100, 200, 500],
  dateFilter: "createdDateTime",
  columns: LEDGER_COLUMNS("balance"),
  actions: { delete: "Delete" },
  formTitle: "Receipt Voucher Entry",
  fields: VOUCHER_FIELDS("Receipt Head", "receipt-head"),
  prepare: isoDates("createdDateTime"),
  uploads: [{ field: "transactionReceipt", path: "upload-scan-copy", idPath: "ledger.id" }],
  successMessage: "Receipt Vourcher Added Successfully",
  stayOnSave: true,
});

define("payment-voucher", "Payment Voucher", "account-transactions", {
  ledger: { uploadPath: "upload-scan-copy" },
  summary: [SUMMARY_DEBIT, SUMMARY_CREDIT, SUMMARY_BALANCE],
  formPath: "/dashboard/transactions/create-payment",
  listParams: { transactionType: "Expense" },
  submitExtras: { transactionType: "Expense" },
  pageSizes: [20, 50, 100, 200, 500],
  dateFilter: "createdDateTime",
  columns: LEDGER_COLUMNS(null),
  actions: { delete: "Delete" },
  formTitle: "Payment Voucher Entry",
  fields: VOUCHER_FIELDS("Payment Head", "payment-head"),
  prepare: isoDates("createdDateTime"),
  uploads: [{ field: "transactionReceipt", path: "upload-scan-copy", idPath: "ledger.id" }],
  successMessage: "Payment Vourcher Added Successfully",
  stayOnSave: true,
});

define("voucher-ledger", "Voucher Ledger", "account-transactions", {
  ledger: { editBalance: false, editTransaction: false },
  summary: [SUMMARY_DEBIT, SUMMARY_CREDIT],
  readOnly: true,
  pageSizes: [20, 50, 100, 200, 500],
  dateFilter: "createdDateTime",
  columns: LEDGER_COLUMNS(null),
});

define("offline-service-transaction", "Offline Service Transaction", "offline-transactions", {
  addLabel: "Add Offline Transaction",
  formPath: "/dashboard/transactions/create-offline-service",
  columns: [
    col("date", "Date", "date"),
    col("service", "Service", "text", { options: SERVICES }),
    col("serviceCategory", "Service Category"),
    col("vendor", "Vendor", "text", { value: (r) => [r.vendor?.identificationShortCode, r.vendor?.vendorName].filter(Boolean).join(" - ") || "N/A" }),
    col("b2bUser", "Agent", "text", { value: (r) => [code(r.b2bUser), r.b2bUser?.companyName].filter(Boolean).join(" - ") || "N/A" }),
    col("vendorPayable", "Vendor Payble", "amount"),
    col("userPayable", "User Payable", "amount"),
    col("profitLoss", "Profit Loss", "amount", { value: (r) => (+r.userPayable || 0) - (+r.vendorPayable || 0) }),
  ],
  details: [col("remarks", "Remarks"), col("createdByUser", "Created By", "name"), col("createdDateTime", "Created Date", "datetime", { fmt: "DD-MMM-YY h:mm A" })],
  actions: { edit: "Edit", delete: "Delete" },
  fields: [
    f("createdDateTime", "Transaction Date", "datetime-local", { required: true, placeholder: "Enter Created Date Time" }),
    f("service", "Service", "select", { required: true, options: ["ALL", ...SERVICES], placeholder: "Select" }),
    f("serviceCategory", "Category", "text", { required: true, placeholder: "Category" }),
    f("vendorTransactionType", "Vendor Transaction Type", "select", { required: true, options: ["CASH", "CREDIT"], placeholder: "Select Transaction Type" }),
    f("vendorId", "Vendor", "relation", { optionsFrom: "vendor-lookup", placeholder: "Search Vendor" }),
    f("vendorPayable", "Vendor Payable", "number", { placeholder: "Enter Vendor Payable" }),
    f("agentTransactionType", "Agent Transaction Type", "select", { required: true, options: ["CASH", "CREDIT"], placeholder: "Select Transaction Type" }),
    f("b2bUserId", "Agent", "relation", { optionsFrom: "agent-lookup", placeholder: "Search Agent" }),
    f("userPayable", "User Payable", "number", { placeholder: "Enter User Payable" }),
    f("remarks", "Remarks", "textarea", { required: true, placeholder: "Enter Remarks" }),
    f("transactionReceipt", "Receipt", "file", { hint: '(No larger than "1 MB")' }),
  ],
  prepare: isoDates("createdDateTime"),
  uploads: [{ field: "transactionReceipt", path: "upload-scan-copy" }],
  successMessage: "Trx Added Successfully",
});

/* ---------------------------------- Loan ---------------------------------- */

const LOAN_PARTY_TYPES = ["Both", "Lender", "Borrower"];
define("loan-party", "Loan Parties", "loan-parties", {
  detailPath: "/dashboard/loan-parties-details",
  actionIcons: [{ label: "Transaction Ledger", to: (row) => `/dashboard/loan-party-ledger-list?loanPartyId=${row.id}` }],
  addLabel: "Add Loan Party",
  formPath: "/dashboard/loan-parties-create",
  tabs: { param: "partyType", items: [{ label: "All", value: "" }, ...LOAN_PARTY_TYPES.map((v) => ({ label: v, value: v }))] },
  saveMessage: "Loan Party",
  columns: [
    col("identificationShortCode", "Party Id", "text", { link: (row) => `/dashboard/loan-parties-details/${row.id}` }),
    col("partyType", "Party Type", "text", { options: LOAN_PARTY_TYPES }),
    col("createdDateTime", "Date", "date", { fmt: "DD-MMM-YY" }),
    col("loanerType", "Loaner Type", "text", { options: ["Bank", "Person", "Company"] }),
    col("personName", "Name"),
    col("personPhoneNo", "Phone Number", "text", { value: (r) => [r.personPhoneCode, r.personPhoneNo].filter(Boolean).join("") || "N/A" }),
    col("occupation", "Occupation"),
    col("companyName", "Company Name"),
    col("bankName", "Bank Name"),
    col("bankAccountNumber", "Account Number"),
    col("isActive", "Status", "status"),
  ],
  details: [col("personEmail", "Email"), col("personAddress", "Address"), col("createdByUser", "Created By", "name"), col("gender", "Gender")],
  actions: { edit: "Edit Loan Party", delete: "Delete Loan Party" },
  detailView: [
    {
      title: "Basic Info",
      items: [
        col("identificationShortCode", "Short Code"),
        col("partyType", "Party Type"),
        col("loanerType", "Loaner Type"),
        col("isActive", "Status", "status"),
      ],
    },
    {
      title: "Contacts Information",
      items: [
        col("personName", "Name"),
        col("gender", "Gender"),
        col("occupation", "Occupation"),
        col("personEmail", "E-mail"),
        col("personPhoneNo", "Mobile", "text", { value: (r) => [r.personPhoneCode, r.personPhoneNo].filter(Boolean).join("") }),
        col("personAddress", "Address"),
      ],
    },
    {
      title: "Company",
      items: [
        col("companyName", "Name"),
        col("companyPhoneNo", "Phone Number", "text", { value: (r) => [r.companyPhoneCode, r.companyPhoneNo].filter(Boolean).join("") }),
        col("companyEmail", "Email"),
        col("companyAddress", "Address"),
      ],
    },
    {
      title: "Bank",
      items: [
        col("bankName", "Name"),
        col("bankAccountName", "Account Name"),
        col("bankAccountNumber", "Account Number"),
        col("bankBranch", "Branch"),
        col("routingNumber", "Routing Number"),
        col("swiftCode", "Swift Code"),
      ],
    },
    {
      title: "Created & Updated Users",
      items: [
        col("createdByUser", "Created By", "user"),
        CREATED_AT,
        col("updatedByUser", "Updated By", "user"),
        col("updatedDateTime", "Updated Date Time", "datetime"),
      ],
    },
  ],
  omitEmpty: true,
  fields: [
    section("Basic Info"),
    f("partyType", "Party Type", "select", { options: LOAN_PARTY_TYPES, placeholder: "Select Party Type" }),
    f("loanerType", "Loaner Type", "select", { options: ["Bank", "Person", "Company"], placeholder: "Select Loaner Type" }),
    f("isActive", "Status", "status"),
    section("Personal Info"),
    f("personName", "Person Name", "text", { showIf: (v) => v.loanerType === "Person" }),
    f("gender", "Gender", "select", { options: ["Male", "Female"], showIf: (v) => v.loanerType === "Person" }),
    f("occupation", "Occupation", "select", {
      showIf: (v) => v.loanerType === "Person",
      options: ["Service (Private/NGO)", "Service (Government)", "Business", "Doctor", "Teacher", "Farmer", "Driver", "Student", "Lawyer", "Retired", "Unemployed", "Housewife"],
    }),
    f("personPhoneCode", "Person Phone Code", "phonecode", { showIf: (v) => v.loanerType === "Person" }),
    f("personPhoneNo", "Person Phone No", "text", { showIf: (v) => v.loanerType === "Person" }),
    f("personEmail", "Person Email", "email", { showIf: (v) => v.loanerType === "Person" }),
    f("personAddress", "Person Address", "text", { showIf: (v) => v.loanerType === "Person" }),
    section("Company Info"),
    f("companyName", "Company Name", "text", { showIf: (v) => v.loanerType === "Company" }),
    f("companyPhoneCode", "Company Phone Code", "phonecode", { showIf: (v) => v.loanerType === "Company" }),
    f("companyPhoneNo", "Company Phone No", "text", { showIf: (v) => v.loanerType === "Company" }),
    f("companyEmail", "Company Email", "email", { showIf: (v) => v.loanerType === "Company" }),
    f("companyAddress", "Company Address", "text", { showIf: (v) => v.loanerType === "Company" }),
    section("Bank Info"),
    f("bankName", "Bank Name", "text", { showIf: (v) => v.loanerType === "Bank" }),
    f("bankAccountName", "Bank Account Name", "text", { showIf: (v) => v.loanerType === "Bank" }),
    f("bankAccountNumber", "Bank Account Number", "text", { showIf: (v) => v.loanerType === "Bank" }),
    f("bankBranch", "Bank Branch", "text", { showIf: (v) => v.loanerType === "Bank" }),
    f("routingNumber", "Routing Number", "text", { showIf: (v) => v.loanerType === "Bank" }),
    f("swiftCode", "Swift Code", "text", { showIf: (v) => v.loanerType === "Bank" }),
  ],
  optionLabel: (r) => `${r.identificationShortCode || ""} - ${r.personName || r.companyName || r.bankName || ""}`,
});

define("loan", "Loan", "loans", {
  detailPath: "/dashboard/loan-details",
  actionIcons: [{ label: "Transaction Ledger", to: (row) => `/dashboard/loan-transaction-list?loanId=${row.id}` }],
  formPath: "/dashboard/loan-create",
  tabs: { param: "status", items: [{ label: "All", value: "" }, { label: "Pending", value: "Pending" }, { label: "Approved", value: "Approved" }, { label: "Rejected", value: "Rejected" }, { label: "Fulfilled", value: "Fulfilled" }, { label: "Closed", value: "Closed" }] },
  saveMessage: "Loan",
  columns: [
    col("identificationShortCode", "Loan Id", "text", { link: (row) => `/dashboard/loan-details/${row.id}` }),
    col("loanParty", "Loan Party", "text", { link: (row) => (row.loanParty?.id ? `/dashboard/loan-parties-details/${row.loanParty.id}` : null), value: (r) => [r.loanParty?.identificationShortCode, r.loanParty?.personName || r.loanParty?.companyName || r.loanParty?.bankName].filter(Boolean).join(" - ") || "N/A" }),
    col("loanType", "Loan Type", "text", { options: ["Personal", "Employee", "General", "Internal"] }),
    col("loanDirection", "Loan Direction", "text", { options: ["Given", "Taken"] }),
    col("noOfInstallment", "No Of Installment", "number"),
    col("principleAmount", "Principle Amount", "amount"),
    col("interestAmount", "Interest Amount", "amount"),
    col("totalAmount", "Total Amount", "amount", { value: (r) => (+r.principleAmount || 0) + (+r.interestAmount || 0) }),
    col("totalPaid", "Total Paid", "amount"),
    col("outstanding", "Outstanding Balance", "amount", { value: (r) => (+r.principleAmount || 0) + (+r.interestAmount || 0) - (+r.totalPaid || 0) }),
    col("status", "Status", "text", { options: ["Pending", "Approved", "Rejected", "Fulfilled", "Closed"] }),
  ],
  details: [col("loanPurpose", "Loan Purpose"), col("createdByUser", "Created By", "name"), col("createdDateTime", "Created Date", "date", { fmt: "DD-MMM-YY" })],
  actions: {
    edit: "Edit Loan",
    delete: "Delete Loan",
    patch: [{ label: "Approve Loan", path: "approve", hideWhen: (r) => r.status === "Approved" }],
    extra: [
      { label: "Disburse / Receive Loan", to: (row) => `/dashboard/create-loan-disburse/new?loanId=${row.id}` },
      { label: "Repayment", to: (row) => `/dashboard/create-loan-repayment/new?loanId=${row.id}` },
    ],
  },
  detailView: [
    {
      title: "Basic Info",
      items: [
        col("identificationShortCode", "Loan Id"),
        col("loanType", "Loan Type"),
        col("loanDirection", "Loan Direction"),
        col("loanCalculationType", "Loan Calculation Type"),
        col("term", "Term"),
        col("principleAmount", "Principle Amount", "amount"),
        col("interestAmount", "Interest Amount", "amount"),
        col("noOfInstallment", "No Of Installment", "number"),
        col("status", "Status"),
      ],
    },
    {
      title: "Loaner Party",
      items: [
        col("loanParty.identificationShortCode", "Short Code"),
        col("loanParty.personName", "Name", "text", { value: (r) => r.loanParty?.personName || r.loanParty?.companyName || r.loanParty?.bankName }),
        col("loanParty.personEmail", "E-mail"),
        col("loanParty.personPhoneNo", "Mobile"),
        col("loanParty.personAddress", "Address"),
      ],
    },
    {
      title: "Loan Info",
      items: [
        col("interestRateInPercentage", "Interest Rate", "text", { value: (r) => (r.interestRateInPercentage != null ? `${r.interestRateInPercentage}%` : "") }),
        col("insPrincipleAmount", "Installment Principle", "amount"),
        col("insInterestAmount", "Installment Interest", "amount"),
        col("installmentAmount", "Installment", "amount"),
        col("outstandingBalance", "Outstanding", "amount"),
      ],
    },
    {
      title: "Dates",
      items: [
        col("applyDate", "Apply Date", "date", { fmt: "DD-MMM-YY" }),
        col("maturityDate", "Fulfilled Date", "date", { fmt: "DD-MMM-YY" }),
        col("firstInstDate", "First Installment Date", "date", { fmt: "DD-MMM-YY" }),
        col("lastInstDate", "Last Installment Date", "date", { fmt: "DD-MMM-YY" }),
        col("disbursementDate", "Disbursement Date", "date", { fmt: "DD-MMM-YY" }),
        col("loanPurpose", "Loan Purpose"),
        col("remarks", "Remarks"),
      ],
    },
    {
      title: "Created & Updated Users",
      items: [
        col("createdByUser", "Created By", "user"),
        CREATED_AT,
        col("updatedByUser", "Updated By", "user"),
        col("updatedDateTime", "Updated Date Time", "datetime"),
      ],
    },
  ],
  omitEmpty: true,
  fields: [
    section("Basic Info"),
    f("loanDirection", "Loan Direction", "select", { required: true, options: ["Given", "Taken"], placeholder: "Select Loan Direction" }),
    f("loanType", "Loan Type", "select", { required: true, options: ["Personal", "Employee", "General", "Internal"] }),
    f("loanPartyId", "Loan Party", "relation", { required: true, optionsFrom: "loan-party", placeholder: "Select Loan Party" }),
    f("loanCalculationType", "Loan Calculation Type", "select", { options: ["Emi", "Short Term", "Straight Line", "Constant Principal"], placeholder: "Select Calculation Type" }),
    section("Loan Amount Info"),
    f("term", "Term", "select", { required: true, options: ["Monthly", "Weekly", "Yearly", "One Time"], placeholder: "Select Term" }),
    f("principleAmount", "Principle Amount", "number", { required: true, placeholder: "Principle Amount" }),
    f("interestRateInPercentage", "Interest Rate %", "number", { required: true, placeholder: "Interest Rate %" }),
    f("interestAmount", "Interest Amount", "number", { required: true, placeholder: "Interest Amount" }),
    section("Installment Info"),
    f("noOfInstallment", "No of Installments", "number", { placeholder: "No of Installments" }),
    f("insPrincipleAmount", "Installment Principle Amount", "number", { placeholder: "Installment Principle" }),
    f("insInterestAmount", "Installment Interest Amount", "number", { placeholder: "Installment Interest" }),
    f("installmentAmount", "Total Installment Amount", "number", { placeholder: "Total Installment Amount" }),
    section("Previous Payments"),
    f("previousPaidPrinciple", "Previous Paid Principle", "number", { placeholder: "Previous Paid Principle" }),
    f("previousPaidInterest", "Previous Paid Interest", "number", { placeholder: "Previous Paid Interest" }),
    section("Dates"),
    f("applyDate", "Apply Date", "date", { placeholder: "Apply Date" }),
    f("maturityDate", "Fulfilled Date", "date", { placeholder: "Fulfilled Date" }),
    f("firstInstDate", "First Installment Date", "date", { placeholder: "First Installment Date" }),
    f("lastInstDate", "Last Installment Date", "date", { placeholder: "Last Installment Date" }),
    f("isDisbursed", "Disbursed", "yesno"),
    f("disbursementDate", "Disbursement Date", "date", { placeholder: "Disbursement Date" }),
    f("remarks", "Remarks", "textarea", { placeholder: "Remarks" }),
    f("status", "Status", "select", { options: ["Rejected", "Fulfilled", "Closed"], placeholder: "Select Status" }),
    f("loanPurpose", "Loan Purpose", "textarea", { placeholder: "Loan Purpose" }),
  ],
  optionLabel: (r) => r.identificationShortCode,
});

const LOAN_LEDGER_COLUMNS = [
  col("createdDateTime", "Transation Date", "datetime"),
  col("referenceId", "Reference ID"),
  col("transactionType", "Type", "text", { options: ["Disbursement", "Repayment"] }),
  col("narration", "Narration"),
  col("debit", "Debit [DR]", "amount"),
  col("credit", "Credit [CR]", "amount"),
  col("principalAmount", "Principal", "amount"),
  col("interestAmount", "Interest", "amount"),
];
define("loan-transaction", "Loan Transactions", "loan-transactions", {
  reconcile: { scopeKey: "loanId", noun: "loan ledger", path: (id) => `loan-transactions/reconciliation/${id}` },
  ledger: {
    balanceBase: "loan-transactions",
    updateBase: "loan-transactions",
    uploadPath: "upload-scan-copy",
    balanceFields: [
      { name: "principalAmount", label: "Principal", type: "number", placeholder: "Principal" },
      { name: "interestAmount", label: "Interest", type: "number", placeholder: "Interest" },
      { name: "notes", label: "Remarks", type: "textarea", required: true, placeholder: "Remarks" },
    ],
    balanceBody: (values, row) => ({
      id: row.id,
      principalAmount: +values.principalAmount || 0,
      interestAmount: +values.interestAmount || 0,
      notes: values.notes,
    }),
  },
  actions: { delete: "Delete" },
  summary: [
    SUMMARY_DEBIT,
    SUMMARY_CREDIT,
    { label: "Principal Paid", value: total("principalAmount") },
    { label: "Interest Paid", value: total("interestAmount") },
    SUMMARY_BALANCE,
  ],
  readOnly: true,
  listPath: "/dashboard/loan-transaction-list",
  dateFilter: "createdDateTime",
  pageSizes: [20, 50, 100, 200, 500],
  columns: LOAN_LEDGER_COLUMNS,
  headerLinks: [
    { label: "Disburse / Receive Loan", to: "/dashboard/create-loan-disburse/new" },
    { label: "Repayment", to: "/dashboard/create-loan-repayment/new" },
  ],
});

// tm: /dashboard/loan-party-ledger?loanPartyId=  (all loan transactions of one party)
define("loan-party-ledger", "Loan Party Ledger", "loan-transactions/loanParyId", {
  readOnly: true,
  listPath: "/dashboard/loan-party-ledger-list",
  dateFilter: "createdDateTime",
  pageSizes: [20, 50, 100, 200, 500],
  columns: [...LOAN_LEDGER_COLUMNS, col("remarks", "Remarks")],
  ledger: { editBalance: false, editTransaction: false },
});

// Forms behind the two tabs of tm's LoanDisburseRepayment page
define("loan-disburse", "Disburse / Receive Loan", "loan-transactions", {
  hidden: true,
  action: "disburse",
  formPath: "/dashboard/create-loan-disburse",
  backTo: "/dashboard/loan-transaction-list",
  fields: [
    f("loanId", "Loan", "relation", { required: true, optionsFrom: "loan", placeholder: "Select Loan" }),
    f("companyBankAccountId", "Company Bank A/c", "relation", { required: true, optionsFrom: "bank-account", placeholder: "Search Bank A/c" }),
    f("amount", "Amount", "number", { required: true, placeholder: "Amount" }),
    f("createdDateTime", "Transaction Date", "datetime-local", { required: true, placeholder: "Enter Created Date Time" }),
    f("bankTransId", "Transation Reference", "text", { placeholder: "Transaction Reference" }),
    f("remarks", "Remarks", "textarea", { placeholder: "Remarks" }),
    f("scanCopy", "Upload Scan Copy", "file"),
  ],
  prepare: isoDates("createdDateTime"),
  uploads: [{ field: "scanCopy", path: "upload-scan-copy", idPath: "ledger.id" }],
  successMessage: "Loan transaction successfully",
  stayOnSave: true,
});
define("loan-repayment", "Repayment", "loan-transactions", {
  hidden: true,
  action: "repayment",
  formPath: "/dashboard/create-loan-repayment",
  backTo: "/dashboard/loan-transaction-list",
  fields: [
    f("loanId", "Loan", "relation", { required: true, optionsFrom: "loan", placeholder: "Select Loan" }),
    f("companyBankAccountId", "Company Bank A/c", "relation", { required: true, optionsFrom: "bank-account", placeholder: "Search Bank A/c" }),
    f("principalAmount", "Principal Amount", "number", { required: true, placeholder: "Principal Amount" }),
    f("interestAmount", "Interest Amount", "number", { placeholder: "Interest Amount" }),
    f("createdDateTime", "Transaction Date", "datetime-local", { required: true, placeholder: "Enter Created Date Time" }),
    f("bankTransId", "Transation Reference", "text", { placeholder: "Transaction Reference" }),
    f("remarks", "Remarks", "textarea", { placeholder: "Remarks" }),
    f("scanCopy", "Upload Scan Copy", "file"),
  ],
  prepare: isoDates("createdDateTime"),
  uploads: [{ field: "scanCopy", path: "upload-scan-copy", idPath: "ledger.id" }],
  successMessage: "Loan transaction successfully",
  stayOnSave: true,
});

/* -------------------------------- Accounts -------------------------------- */

define("bank-account", "Bank Accounts", "company-bank-account", {
  actionIcons: [{ label: "Transaction Ledger", to: (row) => `/dashboard/bank-general-ledger-list?bankAccountId=${row.id}` }],
  addLabel: "Add Bank Account",
  formPath: "/dashboard/company-bank-create",
  pageSizes: [20, 50, 100, 200],
  saveMessage: "Bank Account",
  columns: [
    col("bankType", "Bank Type", "text", { options: ["BANK", "MFS", "CASH"] }),
    col("bankName", "Bank Name"),
    col("bankShortName", "Short Name"),
    col("bankAccountName", "A/c Name"),
    col("accountNumber", "A/c Number"),
    col("bankBranch", "Branch"),
    col("availableBalance", "Available Balance", "amount"),
    col("isShown", "Shown", "yesno"),
    col("serial", "Serial No", "number"),
    col("isChargeApplicable", "Charge Applicable", "yesno"),
    col("gatewayCharge", "Vendor Charge", "number"),
  ],
  details: [col("currency", "Currency"), col("routingNumber", "Routing Number"), col("startingBalance", "Starting Balance", "amount")],
  actions: { edit: "Edit Account", delete: "Delete Account" },
  fields: [
    f("bankType", "Bank Type", "select", { required: true, options: [{ value: "BANK", label: "Bank" }, { value: "MFS", label: "Mobile Bank" }, { value: "CASH", label: "Cash" }], placeholder: "Select Bank Type" }),
    f("bankName", "Bank Name", "text", { required: true, placeholder: "Enter Bank Name" }),
    f("bankShortName", "Bank Short Name", "text", { required: true, placeholder: "Enter Bank Short Name" }),
    f("bankAccountName", "Account Name", "text", { required: true, placeholder: "Enter Account Name" }),
    f("accountNumber", "Account No.", "text", { required: true, placeholder: "Enter Account Number" }),
    f("bankBranch", "Branch Name", "text", { placeholder: "Enter Branch Name" }),
    f("routingNumber", "Routing No.", "text", { placeholder: "Enter Routing Number" }),
    f("startingBalance", "Starting Balance", "number", { placeholder: "Enter Starting Balance" }),
    f("availableBalance", "Available Balance", "number", { placeholder: "Enter Available Balance" }),
    f("currency", "Currency", "text", { placeholder: "Enter Currency (e.g., BDT)" }),
    f("isShown", "Is Shown?", "yesno"),
    f("serial", "Serial No", "number", { placeholder: "Enter Serial No" }),
    f("isGatewayActive", "Is Gateway Active?", "yesno"),
    f("isBanglaQr", "Is Bangla QR?", "yesno"),
    f("isDirectDeposit", "Is Charge Deducted?", "yesno"),
    f("isChargeApplicable", "Is Charge Applicable?", "yesno"),
    f("gatewayCharge", "Vendor Charge", "number", { placeholder: "Enter Vendor Charge" }),
    f("zoneId", "Zone", "relation", { optionsFrom: "zone-lookup", placeholder: "Select Zone" }),
    f("bankLogo", "Bank Logo", "file", { required: true, hint: "(Please upload a document no larger than 1 MB)" }),
  ],
  // tm sends the balances as numbers
  prepare: (payload) => ({
    ...payload,
    startingBalance: +payload.startingBalance || 0,
    availableBalance: +payload.availableBalance || 0,
  }),
  uploads: [{ field: "bankLogo", path: "upload-bank-logo" }],
  optionLabel: (r) => r.bankName,
  seed: [["Islami Bank", "IBBL"], ["City Bank", "CBL"], ["Dutch-Bangla Bank", "DBBL"], ["BRAC Bank", "BRAC"], ["bKash", "BKASH"]].map(([bankName, bankShortName], i) => ({
    bankName, bankShortName, bankType: i === 4 ? "MFS" : "BANK", bankAccountName: "Deshmart Ltd", accountNumber: String(1001200000 + i * 4321), bankBranch: "Gulshan",
    availableBalance: 250000 + i * 50000, startingBalance: 100000, isShown: true, serial: i + 1, isChargeApplicable: i % 2 === 0, gatewayCharge: i % 2 ? 1.5 : 0, currency: "BDT",
  })),
});

define("cheque-history", "Cheque History", "bank-cheque-history", {
  formPath: "/dashboard/bank-cheque-history-create",
  columns: [
    col("createdDateTime", "Created Date", "datetime", { fmt: "DD-MMM-YYYY h:mm A" }),
    col("chequeIssueDate", "Issue Date", "date"),
    col("amount", "Amount", "amount"),
    col("issueType", "Type", "text", { options: ["SEND", "RECEIVE"] }),
    col("bankName", "Bank Name"),
    col("accountName", "Account Name"),
    col("chequeNumber", "Cheque Number"),
    col("chequeReferenceTo", "Cheque Reference To"),
  ],
  details: [col("remarks", "Remarks"), col("createdByUser", "Created By", "name"), col("updatedByUser", "Updated By", "name")],
  actions: {
    edit: "Edit Cheque",
    delete: "Delete Cheque",
    download: [{ label: "Download", keys: ["scanCopyUrl", "fileUrl", "scanCopy"] }],
  },
  fields: [
    f("issueType", "Type", "select", { required: true, options: ["SEND", "RECEIVE"], placeholder: "Select Issue Type" }),
    f("amount", "Amount", "number", { required: true, placeholder: "Enter Amount" }),
    f("bankId", "Bank Name", "relation", { required: true, optionsFrom: "bank-account", placeholder: "Select Company Bank", fillFrom: { bankName: "bankName", accountName: "bankAccountName", accountNumber: "accountNumber" }, transient: true }),
    f("accountName", "Account Name", "text", { placeholder: "Enter Account Name" }),
    f("accountNumber", "Account Number", "text", { placeholder: "Enter Account Number" }),
    f("chequeNumber", "Cheque Number", "text", { required: true, placeholder: "Enter Cheque Number" }),
    f("chequeIssueDate", "Cheque Issue Date", "date", { required: true, placeholder: "Enter Cheque Issue Date" }),
    f("chequeReferenceTo", "Cheque Reference To", "text", { required: true, placeholder: "Enter Cheque Reference To" }),
    f("remarks", "Remarks", "textarea", { required: true, placeholder: "Enter Remarks" }),
    f("file", "Scan Copy", "file", { hint: "(Please upload a document no larger than 1 MB)" }),
  ],
  uploads: [{ field: "file", path: "upload-scan-copy" }],
  successMessage: "Bank cheque history added successfully.",
});

define("online-transaction", "Online Transaction List", "transactions", {
  actions: {
    call: [
      {
        label: "Refund",
        method: "post",
        path: () => "bkash/refund-payment",
        confirm: "Are you sure you want to refund this payment?",
        success: "Refund processed successfully!",
        hideWhen: (row) => String(row.transactionStatus).toLowerCase() !== "completed",
        body: (row) => ({ paymentId: row.paymentId, trxId: row.trxId, amount: row.amount }),
      },
    ],
  },
  readOnly: true,
  dateFilter: "createdDateTime",
  columns: [
    col("provider", "Geteway", "text", { options: ["bKash", "Nagad", "Rocket", "Card"] }),
    col("trxId", "Transaction ID"),
    col("createdDateTime", "Date", "datetime"),
    col("amount", "Amount", "amount"),
    col("charge", "Gateway Fee", "amount"),
    col("totalAmount", "Deposited Amount", "amount"),
    col("b2bUser.companyName", "Company Name"),
    col("customerMsisdn", "Phonen No"),
    col("user", "Staff Name", "user"),
    col("transactionStatus", "Status", "text", { options: ["Completed", "Completed", "Failed", "Pending"] }),
  ],
});

define("bank-general-ledger", "Bank General Ledger", "company-account-ledger", {
  ledger: { balanceBase: "company-bank-account-balance", updateBase: "company-account-ledger", uploadPath: "upload-scan-copy" },
  actions: { delete: "Delete" },
  summary: [SUMMARY_BALANCE],
  readOnly: true,
  pageSizes: [20, 50, 100, 500, 2000, 5000],
  dateFilter: "createdDateTime",
  columns: [
    col("createdDateTime", "Transation Date", "datetime"),
    col("referenceId", "Reference ID"),
    col("transactionType", "Type", "text", { options: ["Income", "Expense", "Deposit"] }),
    col("narration", "Narration"),
    col("debit", "Debit [DR]", "amount"),
    col("credit", "Credit [CR]", "amount"),
    col("currentBalance", "Balance", "amount"),
  ],
});

define("deposit-gateway-fee", "Deposit Gateway Fee", "user-deposit-gateway-fee", {
  formPath: "/dashboard/user-deposit-gateway-fee-create",
  saveMessage: "Deposit Gateway Fee",
  columns: [
    col("amountType", "Amount Type", "text", { options: ["Fixed", "Percentage"] }),
    col("amount", "Amount", "number"),
    col("lowerLimit", "Lower Limit", "number"),
    col("upperLimit", "Upper Limit", "number"),
    col("companyAccounts", "Company Accounts", "text", { value: (r) => (r.companyAccounts || []).map((a) => a.bankName).join(", ") }),
    col("b2bUser", "Agent", "text", { value: (r) => [code(r.b2bUser), r.b2bUser?.companyName].filter(Boolean).join(" - ") }),
    col("userMembershipLevel", "User Membership Level", "text", { options: ["Silver", "Bronze", "Gold", "Platinum"] }),
    col("currency", "Currency", "text", { options: ["BDT"] }),
  ],
  actions: { edit: "Edit", delete: "Delete" },
  fields: [
    f("amountType", "Amount Type", "select", { required: true, options: ["Fixed", "Percentage"] }),
    f("amount", "Amount", "number", { required: true, placeholder: "Enter Amount" }),
    f("lowerLimit", "Lower Limit", "number", { required: true, placeholder: "Enter Lower Limit" }),
    f("upperLimit", "Upper Limit", "number", { placeholder: "Enter Upper Limit" }),
    f("companyAccountIds", "Company Bank A/c", "multirelation", { required: true, optionsFrom: "bank-account", placeholder: "Search Company Bank", prefill: (e) => e.companyAccounts?.map((a) => a.id) }),
    f("b2bUserId", "Agent", "relation", { optionsFrom: "agent-lookup", placeholder: "Search Agent" }),
    f("userMembershipLevel", "Membership Level", "select", { options: ["Silver", "Bronze", "Gold", "Platinum", "Diamond", "Defence", "Corporate", "Government"], placeholder: "Select Membership Level" }),
    f("currency", "Currency", "text"),
  ],
  // tm: an empty upper limit is sent as 0
  prepare: (payload) => ({ ...payload, upperLimit: payload.upperLimit || 0 }),
});

define("agent-general-ledger", "Agent General Ledger", "user-account-ledger", {
  ledger: { balanceBase: "user-account-ledger", updateBase: "user-account-ledger", editTransactionLabel: "Edit Trx Date", uploadPath: "upload-scan-copy" },
  actions: { delete: "Delete" },
  summary: [SUMMARY_BALANCE],
  readOnly: true,
  pageSizes: [20, 50, 100, 200, 500],
  dateFilter: "createdDateTime",
  columns: [
    col("createdDateTime", "Transation Date", "datetime"),
    col("referenceId", "Reference ID"),
    col("transactionType", "Type", "text", { options: ["Deposit", "Booking", "Refund"] }),
    col("service", "Service", "text", { options: SERVICES }),
    col("narration", "Narration"),
    col("debit", "Debit [DR]", "amount"),
    col("credit", "Credit [CR]", "amount"),
    col("currentBalance", "Cash Balance", "amount"),
    col("creditBalance", "Credit Limit", "amount"),
    col("creditUsed", "Credit Used", "amount"),
    col("creditDueAmount", "Credit Due", "amount"),
  ],
});

/* ---------------------------------- Vendor ---------------------------------- */

const walletMoney = (n, currency) => `${(+n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ""}`;
const CURRENCY_CODES = [...new Set(countries.map((c) => c.currency).filter(Boolean))];
const titleCase = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "");

define("vendor", "Vendor", "vendors", {
  // the form loads the whole vendor row - never send the wallet back, the API owns it
  prepare: ({ availableDepositBalance, ...payload }) => payload,
  detailPath: "/dashboard/vendor-details",
  walletPath: "/dashboard/vendor-wallet",
  // header of the Wallet page: current balances in the vendor's settlement currency
  walletSummary: (r) => ({
    title: [r.identificationShortCode, r.vendorName].filter(Boolean).join(" - "),
    pairs: [
      ["Settlement Currency", r.currency || "BDT"],
      ["Wallet Balance", walletMoney(r.availableDepositBalance, r.currency)],
      ["Credit Balance", walletMoney(r.creditBalance, r.currency)],
      ...(r.currency && r.currency !== "BDT" ? [["Total BDT Cost", walletMoney(r.totalBdtCost, "BDT")]] : []),
    ],
  }),
  formPath: "/dashboard/vendor-create",
  addLabel: "Add Vendor",
  saveMessage: "Vendor",
  seed: VENDOR_SEED,
  actionIcons: [{ label: "Transaction Ledger", to: (row) => `/dashboard/vendor-account-ledger-list?vendorId=${row.id}` }],
  filters: [
    { name: "vendorType", label: "Vendor Type", options: ["Offline", "Online"] },
    { name: "status", label: "Status", options: [{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }] },
    { name: "countryCode", label: "Country Code", options: ["BD", "AE", "US", "CA", "GB", "AU"] },
  ],
  // mock mode only: the real API applies these query params itself
  mockFilters: {
    status: (row, value) => String(row.isActive) === value,
    countryCode: (row, value) => row.vendorCountryCode === value,
  },
  columns: [
    col("identificationShortCode", "Vendor ID", "text", { link: (row) => `/dashboard/vendor-details/${row.id}` }),
    col("vendorName", "Full Name"),
    col("vendorType", "Type", "text", { value: (r) => titleCase(r.vendorType) }),
    col("vendorCountryCode", "Country"),
    col("currency", "Currency"),
    col("availableDepositBalance", "Wallet", "amount"),
    col("creditBalance", "Credit", "amount"),
    col("isActive", "Active", "status"),
  ],
  details: [col("identificationShortName", "Short Name"), col("ticketIssuePaymentMode", "Payment Mode")],
  actions: {
    edit: "Edit Profile",
    delete: "Delete",
    extra: [{ label: "View Profile", to: (row) => `/dashboard/vendor-details/${row.id}` }],
  },
  // Wallet transactions + activate / deactivate are attached in pages/Vendor/vendorActions.js
  detailView: [
    {
      title: "Basic Info",
      items: [
        col("identificationShortCode", "Vendor ID"),
        col("vendorName", "Vendor Name"),
        col("vendorType", "Vendor Type"),
        col("identificationShortName", "Vendor Code"),
        col("isAPIprovider", "API Integrated", "yesno"),
        col("isActive", "Status", "status"),
        col("vendorEmail", "Email"),
        col("phoneNo", "Phone No"),
      ],
    },
    {
      title: "Address",
      items: [col("vendorAddress", "Address"), col("vendorCountryCode", "Country"), col("vendorState", "State/Zone"), col("vendorCity", "District"), col("postalCode", "Postal Code")],
    },
    {
      title: "Balance Information",
      items: [
        col("currency", "Settlement Currency"),
        col("ticketIssuePaymentMode", "Payment Mode"),
        col("availableDepositBalance", "Wallet Balance", "amount"),
        col("creditBalance", "Credit Balance", "amount"),
        col("totalBdtCost", "Total BDT Cost", "amount"),
        col("startingDepositBalance", "Opening Wallet Balance", "amount"),
        col("startingBdtCost", "Opening BDT Cost", "amount"),
        col("startingCreditBalance", "Opening Credit Balance", "amount"),
      ],
    },
    {
      title: "Account Information",
      table: {
        columns: [
          col("bankAccountName", "Account Name"),
          col("bankAccountNumber", "Account No."),
          col("bankName", "Bank Name"),
          col("bankShortName", "Bank Short Name"),
          col("accountType", "Bank Type"),
          col("bankBranch", "Branch Name"),
          col("routingNumber", "Routing No."),
          col("swiftCode", "Swift Code"),
        ],
      },
    },
    {
      title: "Contact Information",
      table: {
        columns: [
          col("contactPersonName", "Contact Name"),
          col("contactPersonDesignation", "Designation"),
          col("contactPersonDepartment", "Department"),
          col("contactPersonPhoneNo", "Phone"),
          col("contactPersonEmail", "Email"),
        ],
      },
    },
    {
      title: "Created & Updated Users",
      items: [CREATED_BY, CREATED_AT, col("updatedByUser", "Updated By", "user"), col("updatedDateTime", "Updated Date Time", "datetime")],
    },
  ],
  omitEmpty: true,
  fields: [
    section("Basic Information"),
    f("vendorName", "Vendor Name", "text", { required: true, placeholder: "Vendor Name" }),
    f("identificationShortName", "Vendor Code", "text", { required: true, placeholder: "3 Digit Code" }),
    f("vendorType", "Vendor Type", "select", { required: true, options: ["Online", "Offline"], placeholder: "Select" }),
    f("phoneNo", "Phone No.", "number", { placeholder: "Enter your phone number" }),
    f("vendorEmail", "Email", "email", { placeholder: "Enter your email" }),
    f("isActive", "Active Status", "status", { default: true }),
    f("isAPIprovider", "API Integrated", "yesno", { required: true, default: false }),
    f("vendorAddress", "Address", "text", { placeholder: "Address" }),
    f("vendorCountryCode", "Country", "select", {
      required: true,
      placeholder: "Select",
      options: countries.map((c) => ({ value: c.countryCode, label: c.countryName })),
    }),
    f("vendorState", "State/Zone", "text", { required: true, placeholder: "State/Zone" }),
    f("vendorCity", "District", "text", { required: true, placeholder: "District" }),
    f("postalCode", "Postal Code", "text", { required: true, placeholder: "Postal Code" }),
    section("Account Information"),
    f("bankAccounts", "Bank Accounts", "repeater", {
      itemRequired: ["bankAccountName", "bankAccountNumber", "bankShortName"],
      itemFields: [
        f("bankAccountName", "Account Name", "text", { placeholder: "Enter Account Name" }),
        f("bankAccountNumber", "Account No.", "number", { placeholder: "Enter Account No." }),
        f("bankName", "Bank Name", "text", { placeholder: "Enter Bank Name" }),
        f("bankShortName", "Bank Short Name", "text", { placeholder: "Enter Bank Short Name" }),
        f("accountType", "Bank Type", "select", { options: [{ value: "BANK", label: "Bank" }, { value: "MFS", label: "Mobile Bank" }, { value: "CASH", label: "Cash" }], placeholder: "Select" }),
        f("bankBranch", "Branch Name", "text", { placeholder: "Enter Branch Name" }),
        f("routingNumber", "Routing No.", "number", { placeholder: "Enter Routing Number" }),
        f("swiftCode", "Swift Code", "number", { placeholder: "Enter Swift Code" }),
      ],
    }),
    section("Contact Information"),
    f("contactPersons", "Contact Persons", "repeater", {
      itemRequired: ["contactPersonName", "contactPersonPhoneNo"],
      itemFields: [
        f("contactPersonName", "Contact Name", "text", { placeholder: "Enter Contact Name" }),
        f("contactPersonDesignation", "Designation", "text", { placeholder: "Enter Designation" }),
        f("contactPersonDepartment", "Department", "text", { placeholder: "Enter Department" }),
        f("contactPersonPhoneNo", "Phone", "number", { placeholder: "Enter Phone" }),
        f("contactPersonEmail", "Email", "email", { placeholder: "Enter Email" }),
      ],
    }),
    section("Balance Information"),
    f("currency", "Settlement Currency", "select", { required: true, options: CURRENCY_CODES, placeholder: "Select" }),
    f("ticketIssuePaymentMode", "Payment Mode", "select", {
      required: true,
      default: "CREDIT",
      options: [{ value: "CREDIT", label: "Credit" }, { value: "CASH", label: "Cash" }],
    }),
    f("startingDepositBalance", "Opening Wallet Balance", "number", { placeholder: "Enter wallet Balance" }),
    // non-BDT vendors: same BDT cost + rate calculation as a wallet deposit
    f("startingBdtCost", "Opening BDT Cost (wallet currency)", "number", {
      required: true,
      placeholder: "BDT paid for the opening balance",
      hint: (v) =>
        +v.startingBdtCost > 0 && +v.startingDepositBalance > 0
          ? `Rate: ${(+v.startingBdtCost / +v.startingDepositBalance).toFixed(4)} BDT per 1 ${v.currency}`
          : "Rate = BDT cost / opening wallet balance",
      showIf: (v) => v.currency && v.currency !== "BDT" && Number(v.startingDepositBalance) > 0,
    }),
    f("startingCreditBalance", "Opening Credit Balance", "number", { placeholder: "Enter Credit Balance" }),
    f("logoFile", "Upload Logo", "file"),
  ],
  uploads: [{ field: "logoFile", path: "upload-logo" }],
  optionLabel: (r) => `${r.identificationShortCode || ""} - ${r.vendorName || ""}`,
});

// tm colours the Reference ID by transaction category
const vendorLedgerTone = (r) => {
  const category = r.transactionCategory;
  const type = r.transactionType;
  if ((category === "DEPOSIT" && type !== "ADD") || ["REFUND", "CASHBACK", "OPENING"].includes(category)) return "paid";
  if ((category === "PAYMENT" && type !== "DEDUCT") || category === "CREDIT_SETTLE") return "danger";
  if (category === "CREDIT_ADJUST") return "info";
  if (category === "CREDIT_USE") return "warning";
  return "neutral";
};

define("vendor-account-ledger", "Vendor Account Ledger", "vendor-account-ledgers/findByVendorId", {
  compact: true,
  reconcile: { scopeKey: "vendorId", noun: "vendor ledger", path: (id) => `vendor-balance/reconciliation/${id}` },
  writeEndpoint: "vendor-account-ledgers",
  listPath: "/dashboard/vendor-account-ledger-list",
  banner: "vendor",
  readOnly: true,
  dateFilter: "createdDateTime",
  pageSizes: [10, 20, 50, 100],
  seed: vendorLedgerSeed(),
  summary: [SUMMARY_DEBIT, SUMMARY_CREDIT],
  ledger: {
    balanceBase: "vendor-balance",
    balancePath: "update-ledger-balance",
    updateBase: "vendor-account-ledgers",
    uploadPath: "upload-scan-copy",
    // tm edits debit / credit / credit used together; amount is their sum
    balanceFields: [
      f("debit", "Debit [DR]", "number"),
      f("credit", "Credit [CR]", "number"),
      f("creditUsed", "Credit Used", "number"),
      // a wallet load of a vendor settled in another currency: the BDT paid / rate the Average Rate is built from
      f("bdtAmount", "BDT Amount", "number", { showIf: (v) => v.rateEditable === true, hint: () => "BDT really paid for this load" }),
      f("exchangeRate", "Rate (BDT per 1 unit)", "number", { showIf: (v) => v.rateEditable === true, hint: () => "Change the BDT amount or the rate, not both" }),
      f("notes", "Remarks", "textarea", { required: true, placeholder: "Remarks" }),
    ],
    balanceInitial: (row) => ({
      debit: row.debit ?? 0,
      credit: row.credit ?? 0,
      creditUsed: row.creditUsed ?? 0,
      rateEditable: Boolean(+row.credit > 0 && row.currencyCode && row.currencyCode !== "BDT"),
      bdtAmount: row.bdtAmount ?? "",
      exchangeRate: row.exchangeRate ?? "",
    }),
    // only a changed BDT amount / rate is sent: the rest of the ledger (payments, later loads, the Average Rate)
    // is rebuilt by the backend from them
    balanceBody: (v, row) => ({
      id: row.id,
      amount: (+v.debit || 0) + (+v.credit || 0) + (+v.creditUsed || 0),
      notes: v.notes,
      ...(v.rateEditable && +v.bdtAmount > 0 && +v.bdtAmount !== +row.bdtAmount && { bdtAmount: +v.bdtAmount }),
      ...(v.rateEditable && +v.exchangeRate > 0 && +v.exchangeRate !== +row.exchangeRate && { exchangeRate: +v.exchangeRate }),
    }),
  },
  actions: { delete: "Delete" },
  columns: [
    col("createdDateTime", "Transation Date", "datetime", { group: "first" }),
    col("referenceId", "Reference ID", "text", { group: "first", badge: vendorLedgerTone }),
    col("transactionType", "Type", "text", { group: "first", value: (r) => String(r.transactionType || "").toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) }),
    col("service", "Service", "text", { group: "first", value: (r) => (["wallet", "all"].includes(String(r.service || "").toLowerCase()) ? "" : r.service) }),
    col("narration", "Narration", "text", { group: "first", width: "15%" }),
    col("debit", "Debit [DR]", "amount", { group: "second", currencySuffix: true }),
    col("credit", "Credit [CR]", "amount", { group: "second", currencySuffix: true }),
    col("currentBalance", "Cash Balance", "amount", { group: "second", tint: true, currencySuffix: true }),
    col("creditUsed", "Credit Used", "amount", { group: "third" }),
    col("creditBalance", "Credit Due", "amount", { group: "third" }),
    col("exchangeRate", "Rate", "text", { group: "third", foreignOnly: true, value: (r) => (+r.exchangeRate ? (+r.exchangeRate).toFixed(4) : "-") }),
    col("bdtAmount", "BDT Amount", "text", { group: "third", foreignOnly: true, value: (r) => (+r.bdtAmount ? (+r.bdtAmount).toFixed(2) : "-") }),
    col("runningBdtCost", "Running BDT Cost", "text", { group: "third", foreignOnly: true, value: (r) => (r.currencyCode && r.currencyCode !== "BDT" ? (+r.runningBdtCost || 0).toFixed(2) : "-") }),
    col("settlementStatus", "Settlement", "text", { group: "third", foreignOnly: true, value: (r) => (r.settlementStatus && r.settlementStatus !== "N/A" ? r.settlementStatus : "-") }),
  ],
});

/* ---------------------------------- Refund List ---------------------------------- */
// Every refund of every order - a whole order (FULL) or chosen items (ITEM) - newest first, with the refunded
// items and how the amount was made up. API: adminbeapigateway GET orders/refunds (page, limit, searchString,
// fromDate, toDate, refundType, b2bUserId). Refunds are made from the order page (Refund).
const REFUND_TYPES = [{ value: "FULL", label: "Full order" }, { value: "ITEM", label: "Item wise" }];
const refundTypeLabel = (r) => (r.refundType === "FULL" ? "Full order" : "Item wise");

define("refund", "Refund List", "orders/refunds", {
  readOnly: true,
  dateFilter: "createdDateTime",
  filters: [{ name: "refundType", label: "Type", options: REFUND_TYPES }],
  seed: [
    { id: 1, code: "RF-0001", refundType: "FULL", createdDateTime: "2026-10-20T10:00:00.000Z", orderId: 11, orderCode: "ORD-1042", productTitle: "Wireless Earbuds", customerCode: "AG-1001", customerName: "Sky Travels", itemsAmount: 4200, taxAmount: 0, discountAmount: 200, shippingAmount: 300, otherChargeAmount: 0, totalAmount: 4300, vendorRecoveredAmount: 3800, reason: "Customer cancelled", refundedBy: "Dev Admin", items: [] },
    { id: 2, code: "RF-0002", refundType: "ITEM", createdDateTime: "2026-10-22T12:30:00.000Z", orderId: 12, orderCode: "ORD-1050", productTitle: "Phone Case (3 variations)", customerCode: "AG-1002", customerName: "Green Trade", itemsAmount: 1500, taxAmount: 0, discountAmount: 0, shippingAmount: 0, otherChargeAmount: 0, totalAmount: 1500, vendorRecoveredAmount: 0, reason: "Damaged item", refundedBy: "Dev Admin", items: [{ id: 1, itemName: "Color: Red", quantity: 2, unitAmount: 500, amount: 1000 }, { id: 2, itemName: "Color: Blue", quantity: 1, unitAmount: 500, amount: 500 }] },
  ],
  columns: [
    col("createdDateTime", "Date", "datetime"),
    col("code", "Refund No."),
    col("orderCode", "Order No."),
    col("customerName", "Customer", "text", { value: (r) => [r.customerCode, r.customerName].filter(Boolean).join(" - ") || "-" }),
    col("productTitle", "Product", "text", { value: (r) => r.productTitle || "-" }),
    col("refundType", "Type", "text", { value: refundTypeLabel }),
    col("items", "Items", "text", {
      value: (r) => (r.items?.length ? `${r.items.length} item${r.items.length > 1 ? "s" : ""} / ${r.items.reduce((sum, item) => sum + (+item.quantity || 0), 0)} pcs` : "All items"),
    }),
    col("totalAmount", "Refunded", "amount"),
    col("reason", "Reason", "text", { value: (r) => r.reason || "-" }),
  ],
  details: [
    col("items", "Refunded Items", "text", {
      value: (r) => (r.items?.length ? r.items.map((item) => `${item.itemName || "Item"} x ${item.quantity} = ${taka(item.amount)}`).join(" | ") : "The whole order was refunded"),
    }),
    col("itemsAmount", "Items", "amount"),
    col("taxAmount", "Tax", "amount"),
    col("discountAmount", "Discount", "amount"),
    col("shippingAmount", "Shipping", "amount"),
    col("otherChargeAmount", "Other Charge", "amount"),
    col("vendorRecoveredAmount", "Recovered From Supplier", "amount"),
    col("refundedBy", "Refunded By", "text", { value: (r) => r.refundedBy || "-" }),
    col("remarks", "Remarks", "text", { value: (r) => r.remarks || "-" }),
  ],
});

/* ----------------------------------- Coupon ----------------------------------- */
// Admin-managed coupons, applied at checkout by tmota-backend (conditions combine with AND).
// API: adminbeapigateway /coupons (CRUD; PATCH { isActive } activates / deactivates).

const COUPON_LEVELS = ["Silver", "Bronze", "Gold", "Platinum", "Diamond", "Defence", "Corporate", "Government"];
// sources the checkout sends with a product (apiName / source link)
const COUPON_APIS = ["OSEE", "1688"];
const isPercent = (r) => r.discountType === "PERCENTAGE";
const taka = (n) => `${(+n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} BDT`;
const orAny = (list) => (list && list.length ? list.join(", ") : "Any");
// blank number inputs clear the limit (null); datetime-local -> ISO like tm does
const blankToNull = (payload, keys) => {
  keys.forEach((key) => {
    if (payload[key] === "" || payload[key] === undefined) payload[key] = null;
  });
  return payload;
};

define("coupon", "Coupon", "coupons", {
  formPath: "/dashboard/coupon-create",
  addLabel: "Add Coupon",
  saveMessage: "Coupon",
  filters: [{ name: "isActive", label: "Status", options: [{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }] }],
  seed: [
    { code: "WELCOME10", description: "10% off, up to 500 BDT", discountType: "PERCENTAGE", discountValue: 10, maxDiscountAmount: 500, minPurchaseAmount: 1000, maxPurchaseAmount: null, startDate: "2026-10-01T00:00:00.000Z", endDate: "2026-12-31T23:59:59.000Z", totalUsageLimit: 200, perCustomerUsageLimit: 1, usedCount: 37, customerLevels: [], customerIds: [], apiNames: [] },
    { code: "SAVE100", description: "Flat 100 BDT", discountType: "FIXED", discountValue: 100, maxDiscountAmount: null, minPurchaseAmount: 1500, maxPurchaseAmount: 20000, startDate: "2026-10-05T00:00:00.000Z", endDate: "2026-11-30T23:59:59.000Z", totalUsageLimit: null, perCustomerUsageLimit: 3, usedCount: 12, customerLevels: ["Gold", "Platinum"], customerIds: [], apiNames: [] },
    { code: "OSEE5", description: "5% off OSEE products", discountType: "PERCENTAGE", discountValue: 5, maxDiscountAmount: 300, minPurchaseAmount: null, maxPurchaseAmount: null, startDate: null, endDate: null, totalUsageLimit: 100, perCustomerUsageLimit: null, usedCount: 100, customerLevels: [], customerIds: [], apiNames: ["OSEE"], isActive: false },
    { code: "VIP250", description: "VIP customers only", discountType: "FIXED", discountValue: 250, maxDiscountAmount: null, minPurchaseAmount: 3000, maxPurchaseAmount: null, startDate: "2026-09-01T00:00:00.000Z", endDate: "2026-09-30T23:59:59.000Z", totalUsageLimit: 50, perCustomerUsageLimit: 1, usedCount: 8, customerLevels: [], customerIds: [5], customers: [{ id: 5, identificationShortCode: "AG-1002", companyName: "Sky Travels" }], apiNames: [] },
  ].map((coupon) => ({ isActive: true, customers: [], ...coupon })),
  columns: [
    col("code", "Code"),
    col("discountType", "Discount", "text", { value: (r) => (isPercent(r) ? `${+r.discountValue || 0}%${r.maxDiscountAmount ? ` (max ${taka(r.maxDiscountAmount)})` : ""}` : taka(r.discountValue)) }),
    col("minPurchaseAmount", "Min Purchase", "text", { value: (r) => (r.minPurchaseAmount ? taka(r.minPurchaseAmount) : "-") }),
    col("maxPurchaseAmount", "Max Purchase", "text", { value: (r) => (r.maxPurchaseAmount ? taka(r.maxPurchaseAmount) : "-") }),
    col("startDate", "Valid From", "date", { fmt: "DD-MMM-YY" }),
    col("endDate", "Valid To", "date", { fmt: "DD-MMM-YY" }),
    col("usedCount", "Used", "text", { value: (r) => `${r.usedCount || 0}${r.totalUsageLimit ? ` / ${r.totalUsageLimit}` : ""}` }),
    ACTIVE,
  ],
  details: [
    col("description", "Description"),
    col("customerLevels", "Customer Levels", "text", { value: (r) => orAny(r.customerLevels) }),
    col("customers", "Customers", "text", {
      value: (r) => orAny((r.customers || []).map((c) => [c.identificationShortCode, c.companyName].filter(Boolean).join(" - "))),
    }),
    col("apiNames", "APIs", "text", { value: (r) => orAny(r.apiNames) }),
    col("perCustomerUsageLimit", "Per Customer Limit", "text", { value: (r) => (r.perCustomerUsageLimit ? `${r.perCustomerUsageLimit}` : "Unlimited") }),
    CREATED_AT,
  ],
  actions: { edit: "Edit Coupon", delete: "Delete Coupon" },
  fields: [
    section("Coupon"),
    f("code", "Coupon Code", "text", { required: true, placeholder: "e.g. WELCOME10" }),
    f("description", "Description", "text", { placeholder: "Shown to the team only" }),
    f("discountType", "Discount Type", "select", {
      required: true,
      placeholder: "Select Discount Type",
      options: [{ value: "FIXED", label: "Fixed amount (BDT)" }, { value: "PERCENTAGE", label: "Percentage (%)" }],
    }),
    f("discountValue", "Discount Value", "number", {
      required: true,
      placeholder: "Amount or percent",
      hint: (v) => (v.discountType === "PERCENTAGE" ? "Percent, 0 - 100" : v.discountType === "FIXED" ? "Taka taken off the order" : undefined),
    }),
    f("maxDiscountAmount", "Max Discount Amount (BDT)", "number", {
      placeholder: "Cap for the percentage discount (optional)",
      showIf: (v) => v.discountType === "PERCENTAGE",
    }),
    f("isActive", "Status", "status", { default: true }),
    section("Purchase Amount (optional)"),
    f("minPurchaseAmount", "Minimum Purchase Amount", "number", { placeholder: "Order total at least" }),
    f("maxPurchaseAmount", "Maximum Purchase Amount", "number", { placeholder: "Order total at most" }),
    section("Valid Between (optional)"),
    f("startDate", "Start Date", "datetime-local"),
    f("endDate", "End Date", "datetime-local"),
    section("Usage Limit (optional)"),
    f("totalUsageLimit", "Total Usage Limit", "number", { placeholder: "Times the coupon can be used in total" }),
    f("perCustomerUsageLimit", "Per Customer Usage Limit", "number", { placeholder: "Times one customer can use it" }),
    section("Conditions (optional, leave empty for everyone)"),
    f("customerLevels", "Customer Levels", "checkboxes", { options: COUPON_LEVELS, default: [] }),
    f("customerIds", "Customers", "multirelation", { optionsFrom: "agent-lookup", placeholder: "Select Customers", prefill: (e) => e.customerIds || (e.customers || []).map((c) => c.id) }),
    f("apiNames", "APIs / Suppliers", "checkboxes", { options: COUPON_APIS, default: [] }),
  ],
  // empty limits / dates clear the value on edit; dates go as ISO strings
  prepare: (payload) => {
    const body = blankToNull({ ...payload }, ["maxDiscountAmount", "minPurchaseAmount", "maxPurchaseAmount", "startDate", "endDate", "totalUsageLimit", "perCustomerUsageLimit"]);
    ["startDate", "endDate"].forEach((key) => {
      if (body[key]) body[key] = new Date(body[key]).toISOString();
    });
    if (body.code) body.code = String(body.code).trim().toUpperCase();
    return body;
  },
  optionLabel: (r) => r.code,
});

/* ------------------------------- Product Discount ------------------------------- */
// Discounts on the product prices of the web shop: every product or the products of chosen shop categories /
// subcategories (picked from the shop category menu). dm-admin-backend /product-discounts (CRUD; PATCH { isActive }
// activates / deactivates); tmota applies them to the prices of the product lists and the product page (so the cart
// and checkout see the same price). They never add up: highest priority wins, then Category > All, then the biggest
// saving.

const DISCOUNT_SCOPES = [
  { value: "ALL", label: "All products" },
  { value: "CATEGORY", label: "Product categories" },
];
const scopeLabel = (scope) => DISCOUNT_SCOPES.find((x) => x.value === scope)?.label || scope;
// the shop category / subcategory name of a search tag
const CATEGORY_NAMES = new Map(SHOP_CATEGORIES.flatMap((category) => [[category.tag, category.name], ...category.subs]));
const categoryNames = (tags) => [...new Set((tags || []).map((tag) => CATEGORY_NAMES.get(tag) || tag))];

define("product-discount", "Product Discount", "product-discounts", {
  formPath: "/dashboard/product-discount-create",
  addLabel: "Add Product Discount",
  saveMessage: "Product discount",
  filters: [
    { name: "scope", label: "Applies To", options: DISCOUNT_SCOPES },
    { name: "isActive", label: "Status", options: [{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }] },
  ],
  seed: [
    { name: "Eid sale", scope: "ALL", discountType: "PERCENTAGE", discountValue: 5, maxDiscountAmount: 200, startDate: "2026-10-01T00:00:00.000Z", endDate: "2026-12-31T23:59:59.000Z", priority: 0, categoryIds: [] },
    { name: "Bags clearance", scope: "CATEGORY", discountType: "PERCENTAGE", discountValue: 12, maxDiscountAmount: null, startDate: null, endDate: null, priority: 1, categoryIds: ["bags", "purse", "wallet"] },
    { name: "Shoes offer", scope: "CATEGORY", discountType: "FIXED", discountValue: 50, maxDiscountAmount: null, startDate: null, endDate: null, priority: 2, categoryIds: ["shoes", "men-shoes", "sports-shoes"], isActive: false },
  ].map((discount) => ({ isActive: true, ...discount })),
  columns: [
    col("name", "Name"),
    col("scope", "Applies To", "text", {
      value: (r) => {
        const names = r.scope === "CATEGORY" ? categoryNames(r.categoryIds) : [];
        return names.length ? `${scopeLabel(r.scope)} (${names.length})` : scopeLabel(r.scope);
      },
    }),
    col("discountType", "Discount", "text", { value: (r) => (isPercent(r) ? `${+r.discountValue || 0}%${r.maxDiscountAmount ? ` (max ${taka(r.maxDiscountAmount)})` : ""}` : taka(r.discountValue)) }),
    col("priority", "Priority", "text", { value: (r) => `${r.priority || 0}` }),
    col("startDate", "Valid From", "date", { fmt: "DD-MMM-YY" }),
    col("endDate", "Valid To", "date", { fmt: "DD-MMM-YY" }),
    ACTIVE,
  ],
  details: [
    col("categoryIds", "Categories", "text", { value: (r) => (r.categoryIds?.length ? categoryNames(r.categoryIds).join(", ") : "-") }),
    CREATED_AT,
  ],
  actions: { edit: "Edit Product Discount", delete: "Delete Product Discount" },
  fields: [
    section("Discount"),
    f("name", "Name", "text", { required: true, placeholder: "e.g. Eid sale" }),
    f("scope", "Applies To", "select", { required: true, placeholder: "Select what it applies to", options: DISCOUNT_SCOPES, default: "ALL" }),
    f("categoryIds", "Categories", "category-tree", {
      required: true,
      categories: SHOP_CATEGORIES,
      default: [],
      showIf: (v) => v.scope === "CATEGORY",
      hint: "Pick a category for all its subcategories, or single subcategories",
    }),
    f("discountType", "Discount Type", "select", {
      required: true,
      placeholder: "Select Discount Type",
      options: [{ value: "PERCENTAGE", label: "Percentage (%)" }, { value: "FIXED", label: "Fixed amount per piece (BDT)" }],
    }),
    f("discountValue", "Discount Value", "number", {
      required: true,
      placeholder: "Percent or amount",
      hint: (v) => (v.discountType === "PERCENTAGE" ? "Percent, 0 - 100" : v.discountType === "FIXED" ? "Taka taken off one piece" : undefined),
    }),
    f("maxDiscountAmount", "Max Discount Amount (BDT)", "number", {
      placeholder: "Cap per piece for the percentage discount (optional)",
      showIf: (v) => v.discountType === "PERCENTAGE",
    }),
    f("priority", "Priority", "number", { default: 0, hint: "When discounts overlap the higher priority wins (they never add up)" }),
    f("isActive", "Status", "status", { default: true }),
    section("Valid Between (optional)"),
    f("startDate", "Start Date", "datetime-local"),
    f("endDate", "End Date", "datetime-local"),
  ],
  // the category tags go only with the Product categories scope; empty cap / dates clear the value on edit; dates go as ISO strings
  prepare: (payload) => {
    const body = blankToNull({ ...payload }, ["maxDiscountAmount", "startDate", "endDate"]);
    ["startDate", "endDate"].forEach((key) => {
      if (body[key]) body[key] = new Date(body[key]).toISOString();
    });
    body.categoryIds = body.scope === "CATEGORY" ? body.categoryIds || [] : [];
    if (body.discountType !== "PERCENTAGE") body.maxDiscountAmount = null;
    if (body.priority === "" || body.priority === undefined || body.priority === null) body.priority = 0;
    return body;
  },
  optionLabel: (r) => r.name,
});

/* ------------------------------- Product Markup ------------------------------- */
// Markup added to the supplier prices of the products (tm: Product Markup, /dashboard/product-commission-list and
// /dashboard/create-product-markup-commission). API: dm-admin-backend /product-user-markup-commission. tmota hands the
// rules to the product service, which marks up every price of the web shop with the best matching rule (API provider,
// category, user type and membership level), so the web shop shows the marked-up prices.

const MARKUP_USER_TYPES = ["B2B", "B2C"];
const apiProviderName = (p) => (p ? `${p.providerShortCodeName || ""} - ${p.providerName || ""}` : "N/A");
const na = (value) => (value === undefined || value === null || value === "" ? "N/A" : value);

lookup("api-provider-lookup", "vendor-api-provider", (r) => apiProviderName(r),
  [col("providerShortCodeName", "Code"), col("providerName", "Provider")],
  [{ providerShortCodeName: "OSEE", providerName: "OSEE 1688" }, { providerShortCodeName: "TML", providerName: "Tourmart" }]);

define("product-markup", "Product Markup", "product-user-markup-commission", {
  listPath: "/dashboard/product-commission-list",
  formPath: "/dashboard/create-product-markup-commission",
  addLabel: "Add Product Markup",
  saveMessage: "Product markup",
  seed: [
    { title: "OSEE standard", category: "", apiProviderId: 1, apiProvider: { providerShortCodeName: "OSEE", providerName: "OSEE 1688" }, userType: "B2B", userMembershipLevel: "Silver", markupType: "Percentage", markupValue: 10, currency: "BDT", priority: 1 },
    { title: "OSEE gold", category: "", apiProviderId: 1, apiProvider: { providerShortCodeName: "OSEE", providerName: "OSEE 1688" }, userType: "B2B", userMembershipLevel: "Gold", markupType: "Percentage", markupValue: 7, currency: "BDT", priority: 2 },
    { title: "Flat handling", category: "BAGS", apiProviderId: 2, apiProvider: { providerShortCodeName: "TML", providerName: "Tourmart" }, userType: "B2C", userMembershipLevel: "", markupType: "Fixed", markupValue: 50, currency: "BDT", priority: 3 },
  ],
  columns: [
    col("title", "Title", "text", { value: (r) => na(r.title) }),
    col("category", "Category", "text", { value: (r) => na(r.category) }),
    col("apiProvider", "API Provider", "text", { value: (r) => apiProviderName(r.apiProvider) }),
    col("userType", "User Type", "text", { value: (r) => na(r.userType) }),
    col("userMembershipLevel", "Membership Level", "text", { value: (r) => na(r.userMembershipLevel) }),
    col("markupValue", "Markup Value", "text", { value: (r) => r.markupValue || "0" }),
    col("markupType", "Markup Type", "text", { value: (r) => na(r.markupType) }),
    col("currency", "Currency", "text", { value: (r) => na(r.currency) }),
    col("priority", "Priority", "text", { value: (r) => na(r.priority) }),
  ],
  details: [
    col("createdByUser", "Created By", "name"),
    col("createdDateTime", "Created Date", "datetime"),
    col("updatedByUser", "Updated By", "name"),
    col("updatedDateTime", "Updated Date", "datetime"),
  ],
  actions: {
    edit: "Edit Markup",
    delete: "Delete Markup",
    patch: [{ label: "Duplicate Markup", path: "duplicate" }],
  },
  fields: [
    section("Product User Markup", "Update Product User Markup"),
    f("title", "Title", "text", { placeholder: "Title" }),
    f("category", "Category", "text", { placeholder: "Category" }),
    f("apiProviderId", "API Provider", "relation", { optionsFrom: "api-provider-lookup", placeholder: "Select API Provider", prefill: (e) => e.apiProviderId ?? e.apiProvider?.id ?? "" }),
    f("userType", "User Type", "select", { options: MARKUP_USER_TYPES, placeholder: "Select User Type" }),
    f("userMembershipLevel", "User Membership Level", "select", { options: COUPON_LEVELS, placeholder: "Select Membership Level" }),
    f("markupValue", "Markup Value", "number", { placeholder: "Markup Value", hint: (v) => (v.markupType === "Percentage" ? "Percent, 0 - 100" : undefined) }),
    f("markupType", "Markup Type", "select", { options: ["Fixed", "Percentage"], placeholder: "Select Markup Type" }),
    f("priority", "Priority", "number", { placeholder: "Priority" }),
    f("currency", "Currency", "select", { options: [{ value: "BDT", label: "BDT (৳)" }, { value: "CNY", label: "CNY (¥)" }], placeholder: "Select Currency" }),
  ],
  // blank number fields are left out (tm sends undefined); ids / values go as numbers
  prepare: (payload) => {
    const body = { ...payload };
    ["markupValue", "priority", "apiProviderId"].forEach((key) => {
      body[key] = body[key] === "" || body[key] === null || body[key] === undefined || Number.isNaN(Number(body[key])) ? undefined : Number(body[key]);
    });
    return body;
  },
  optionLabel: (r) => r.title,
});

/* ------------------------------- Reward Points ------------------------------- */
// Admin-configurable reward points. API: adminbeapigateway /reward/*
//   rules              : earning rules (conditions combine with AND; highest priority wins, no stacking unless allowed)
//   conversion-settings: points -> cash rate per customer level / type (+ minimum / block of points to redeem)
//   wallets            : every customer's balance + cash value, Adjust Points (manual add / deduct, remark required)
//   transactions       : reward report (date range, level, type, kind) with the order, rule and running balance
// Rows are earned when an order is Completed and taken back on cancel / refund (backend), redeemed by the customer.

const REWARD_KINDS = [
  { value: "EARN", label: "Earned" },
  { value: "REDEEM", label: "Redeemed" },
  { value: "REVERSE", label: "Reversed" },
  { value: "ADJUST_ADD", label: "Manual Add" },
  { value: "ADJUST_DEDUCT", label: "Manual Deduct" },
];
const rewardKind = (type) => REWARD_KINDS.find((kind) => kind.value === type)?.label || type;
const points = (n) => (+n || 0).toLocaleString();
const signedPoints = (n) => `${+n > 0 ? "+" : ""}${points(n)}`;
const levelFilter = { name: "membershipLevel", label: "Level", options: COUPON_LEVELS };

define("reward-rule", "Reward Rule", "reward/rules", {
  formPath: "/dashboard/reward-rule-create",
  addLabel: "Add Reward Rule",
  saveMessage: "Reward rule",
  filters: [{ name: "isActive", label: "Status", options: [{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }] }],
  seed: [
    { name: "1 point per 10 BDT", description: "Default earning", ruleType: "PER_AMOUNT", points: 1, perAmount: 10, maxPointsPerOrder: 500, minPurchaseAmount: null, maxPurchaseAmount: null, startDate: null, endDate: null, priority: 1, allowStacking: false, customerLevels: [], customerIds: [], apiNames: [] },
    { name: "Gold double", description: "Gold customers earn more", ruleType: "PER_AMOUNT", points: 2, perAmount: 10, maxPointsPerOrder: null, minPurchaseAmount: 1000, maxPurchaseAmount: null, startDate: "2026-10-01T00:00:00.000Z", endDate: "2026-12-31T23:59:59.000Z", priority: 5, allowStacking: false, customerLevels: ["Gold", "Platinum"], customerIds: [], apiNames: [] },
    { name: "50 points per order", description: "Flat bonus", ruleType: "FIXED", points: 50, perAmount: null, maxPointsPerOrder: null, minPurchaseAmount: 2000, maxPurchaseAmount: null, startDate: null, endDate: null, priority: 0, allowStacking: true, customerLevels: [], customerIds: [], apiNames: ["OSEE"], isActive: false },
  ].map((rule) => ({ isActive: true, ...rule })),
  columns: [
    col("name", "Rule"),
    col("ruleType", "Earns", "text", { value: (r) => (r.ruleType === "FIXED" ? `${points(r.points)} points per purchase` : `${points(r.points)} point per ${taka(r.perAmount)}`) }),
    col("maxPointsPerOrder", "Max / Order", "text", { value: (r) => (r.maxPointsPerOrder ? points(r.maxPointsPerOrder) : "-") }),
    col("minPurchaseAmount", "Min Purchase", "text", { value: (r) => (r.minPurchaseAmount ? taka(r.minPurchaseAmount) : "-") }),
    col("startDate", "Valid From", "date", { fmt: "DD-MMM-YY" }),
    col("endDate", "Valid To", "date", { fmt: "DD-MMM-YY" }),
    col("priority", "Priority"),
    ACTIVE,
  ],
  details: [
    col("description", "Description"),
    col("customerLevels", "Customer Levels", "text", { value: (r) => orAny(r.customerLevels) }),
    col("customerIds", "Customers", "text", { value: (r) => ((r.customerIds || []).length ? (r.customerIds || []).map((id) => `#${id}`).join(", ") : "Any") }),
    col("apiNames", "APIs", "text", { value: (r) => orAny(r.apiNames) }),
    col("maxPurchaseAmount", "Max Purchase", "text", { value: (r) => (r.maxPurchaseAmount ? taka(r.maxPurchaseAmount) : "-") }),
    col("allowStacking", "Stacking", "text", { value: (r) => (r.allowStacking ? "Adds to other stacking rules" : "Highest priority only") }),
    CREATED_AT,
  ],
  actions: { edit: "Edit Reward Rule", delete: "Delete Reward Rule" },
  fields: [
    section("Reward Rule"),
    f("name", "Rule Name", "text", { required: true, placeholder: "e.g. 1 point per 10 BDT" }),
    f("description", "Description", "text", { placeholder: "Shown to the team only" }),
    f("ruleType", "Earning Type", "select", {
      required: true,
      placeholder: "Select Earning Type",
      options: [{ value: "FIXED", label: "Fixed points per purchase" }, { value: "PER_AMOUNT", label: "Points per amount spent" }],
    }),
    f("points", "Points", "number", {
      required: true,
      placeholder: "Points",
      hint: (v) => (v.ruleType === "PER_AMOUNT" ? "Points earned for every amount below" : v.ruleType === "FIXED" ? "Points earned by each qualifying order" : undefined),
    }),
    f("perAmount", "Per Amount Spent (BDT)", "number", { required: true, placeholder: "e.g. 10", showIf: (v) => v.ruleType === "PER_AMOUNT", hint: () => "1 point per 10 BDT = points 1, amount 10" }),
    f("maxPointsPerOrder", "Max Points Per Order", "number", { placeholder: "Cap for one order (optional)" }),
    f("priority", "Priority", "number", { default: 0, hint: () => "When rules overlap the higher priority wins" }),
    f("allowStacking", "Allow Stacking", "yesno", { default: false }),
    f("isActive", "Status", "status", { default: true }),
    section("Purchase Amount (optional)"),
    f("minPurchaseAmount", "Minimum Purchase Amount", "number", { placeholder: "Order total at least" }),
    f("maxPurchaseAmount", "Maximum Purchase Amount", "number", { placeholder: "Order total at most" }),
    section("Valid Between (optional)"),
    f("startDate", "Start Date", "datetime-local"),
    f("endDate", "End Date", "datetime-local"),
    section("Conditions (optional, leave empty for everyone)"),
    f("customerLevels", "Customer Levels", "checkboxes", { options: COUPON_LEVELS, default: [] }),
    f("customerIds", "Customers", "multirelation", { optionsFrom: "agent-lookup", placeholder: "Select Customers", prefill: (e) => e.customerIds || [] }),
    f("apiNames", "APIs / Suppliers", "checkboxes", { options: COUPON_APIS, default: [] }),
  ],
  prepare: (payload) => {
    const body = blankToNull({ ...payload }, ["maxPointsPerOrder", "minPurchaseAmount", "maxPurchaseAmount", "startDate", "endDate"]);
    ["startDate", "endDate"].forEach((key) => {
      if (body[key]) body[key] = new Date(body[key]).toISOString();
    });
    if (body.ruleType !== "PER_AMOUNT") delete body.perAmount;
    ["points", "perAmount", "priority", "maxPointsPerOrder", "minPurchaseAmount", "maxPurchaseAmount"].forEach((key) => {
      if (body[key] !== null && body[key] !== undefined && body[key] !== "") body[key] = +body[key];
    });
    return body;
  },
  optionLabel: (r) => r.name,
});

define("reward-conversion", "Conversion Settings", "reward/conversion-settings", {
  formPath: "/dashboard/reward-conversion-create",
  addLabel: "Add Conversion Rate",
  saveMessage: "Conversion rate",
  seed: [
    { membershipLevel: null, points: 500, cashAmount: 10, minRedeemPoints: 500, redeemBlockPoints: 100 },
    { membershipLevel: "Gold", points: 400, cashAmount: 10, minRedeemPoints: 400, redeemBlockPoints: null },
    { membershipLevel: null, points: 300, cashAmount: 10, minRedeemPoints: 300, redeemBlockPoints: null },
  ].map((setting) => ({ isActive: true, ...setting })),
  columns: [
    col("membershipLevel", "Customer Level", "text", { value: (r) => r.membershipLevel || "All levels" }),
    col("points", "Rate", "text", { value: (r) => `${points(r.points)} points = ${taka(r.cashAmount)}` }),
    col("minRedeemPoints", "Min Redeem", "text", { value: (r) => (r.minRedeemPoints ? `${points(r.minRedeemPoints)} points` : "-") }),
    col("redeemBlockPoints", "Redeem In Blocks Of", "text", { value: (r) => (r.redeemBlockPoints ? `${points(r.redeemBlockPoints)} points` : "Any amount") }),
    ACTIVE,
  ],
  actions: { edit: "Edit Conversion Rate", delete: "Delete Conversion Rate" },
  fields: [
    section("Conversion Rate (applies to future redemptions only)"),
    f("membershipLevel", "Customer Level", "select", { placeholder: "All levels", options: COUPON_LEVELS }),
    f("points", "Points", "number", { required: true, placeholder: "e.g. 500" }),
    f("cashAmount", "Cash Value (BDT)", "number", { required: true, placeholder: "e.g. 10", hint: (v) => (v.points && v.cashAmount ? `${points(v.points)} points = ${taka(v.cashAmount)}` : undefined) }),
    f("minRedeemPoints", "Minimum Points To Redeem", "number", { default: 0 }),
    f("redeemBlockPoints", "Redeem In Blocks Of (optional)", "number", { placeholder: "e.g. 100 = only multiples of 100" }),
    f("isActive", "Status", "status", { default: true }),
  ],
  prepare: (payload) => {
    const body = blankToNull({ ...payload }, ["membershipLevel", "redeemBlockPoints"]);
    ["points", "cashAmount", "minRedeemPoints", "redeemBlockPoints"].forEach((key) => {
      if (body[key] !== null && body[key] !== undefined && body[key] !== "") body[key] = +body[key];
    });
    return body;
  },
});

define("reward-wallet", "Reward Wallets", "reward/wallets", {
  readOnly: true,
  filters: [levelFilter],
  seed: [
    { b2bUserId: 1, identificationShortCode: "AG-1001", companyName: "Sky Travels", membershipLevel: "Gold", pointsBalance: 1850, totalEarned: 2600, totalRedeemed: 750, cashValue: 46.25 },
    { b2bUserId: 2, identificationShortCode: "AG-1002", companyName: "Green Trade", membershipLevel: "Silver", pointsBalance: 420, totalEarned: 420, totalRedeemed: 0, cashValue: 8.4 },
  ],
  columns: [
    col("identificationShortCode", "Code"),
    col("companyName", "Customer"),
    col("membershipLevel", "Level"),
    col("pointsBalance", "Points Balance", "text", { value: (r) => points(r.pointsBalance) }),
    col("cashValue", "Cash Value", "text", { value: (r) => taka(r.cashValue) }),
    col("totalEarned", "Total Earned", "text", { value: (r) => points(r.totalEarned) }),
    col("totalRedeemed", "Total Redeemed", "text", { value: (r) => points(r.totalRedeemed) }),
  ],
});

define("reward-transaction", "Reward Report", "reward/transactions", {
  readOnly: true,
  dateFilter: "createdDateTime",
  filters: [levelFilter, { name: "type", label: "Kind", options: REWARD_KINDS }],
  seed: [
    { createdDateTime: "2026-10-20T10:00:00.000Z", customer: { identificationShortCode: "AG-1001", companyName: "Sky Travels", membershipLevel: "Gold" }, type: "EARN", points: 300, balanceAfter: 2600, orderCode: "ORD-1042", ruleName: "Gold double", remarks: "Points for order ORD-1042" },
    { createdDateTime: "2026-10-22T12:30:00.000Z", customer: { identificationShortCode: "AG-1001", companyName: "Sky Travels", membershipLevel: "Gold" }, type: "REDEEM", points: -750, balanceAfter: 1850, remarks: "Redeemed for 15 BDT" },
    { createdDateTime: "2026-10-23T09:15:00.000Z", customer: { identificationShortCode: "AG-1002", companyName: "Green Trade", membershipLevel: "Silver" }, type: "ADJUST_ADD", points: 100, balanceAfter: 520, remarks: "Goodwill" },
    { createdDateTime: "2026-10-24T08:00:00.000Z", customer: { identificationShortCode: "AG-1002", companyName: "Green Trade", membershipLevel: "Silver" }, type: "REVERSE", points: -100, balanceAfter: 420, orderCode: "ORD-1050", ruleName: "1 point per 10 BDT", remarks: "Order ORD-1050 cancelled / refunded" },
  ].map((row, index) => ({ id: index + 1, orderCode: null, ruleName: null, ...row })),
  columns: [
    col("createdDateTime", "Date", "date", { fmt: "DD-MMM-YY h:mm A" }),
    col("customer", "Customer", "text", { value: (r) => [r.customer?.identificationShortCode, r.customer?.companyName].filter(Boolean).join(" - ") }),
    col("level", "Level", "text", { value: (r) => r.customer?.membershipLevel || "-" }),
    col("orderCode", "Order No.", "text", { value: (r) => r.orderCode || "-" }),
    col("type", "Kind", "text", { value: (r) => rewardKind(r.type) }),
    col("ruleName", "Rule", "text", { value: (r) => r.ruleName || "-" }),
    col("points", "Points", "text", { value: (r) => signedPoints(r.points) }),
    col("balanceAfter", "Balance", "text", { value: (r) => points(r.balanceAfter) }),
    col("remarks", "Remarks", "text", { value: (r) => r.remarks || "-" }),
  ],
});

/* --------------------------------- Customer --------------------------------- */
// The "Customer" of this admin is tm-admin-web-react's Agent (B2B account): pages/Agent
// (AgentListTable list + tabs, the Agency / User / Documents / Status steps as one form,
// AgentDetails profile, Agent Account wallet tabs and the agent ledger).
// Wallet actions + approval / activation actions are attached in pages/Customer/customerActions.js.

const GEO = countryStates[0].countries;
const geoCountry = (v) => GEO.find((c) => c.alpha2Code === v.companyCountryCode);
const geoState = (v) => geoCountry(v)?.states?.find((s) => s.name === v.companyState);
const MEMBERSHIP_LEVELS = ["Silver", "Bronze", "Gold", "Platinum", "Diamond", "Defence", "Corporate", "Government"];
const YES_NO = [{ value: true, label: "Yes" }, { value: false, label: "No" }];
const agentUser = (r) => r.b2bAgencyUser?.[0]?.user || {};
const agentWallet = (r) => r.b2bUserWallet?.wallet || {};
const agencyPhone = (r) => [r.companyPhoneCode, r.companyPhoneNo].filter(Boolean).join(" ");

// tm sends the Agency step + the Primary User step + the Status step (the last one after the agent exists)
const CUSTOMER_AGENCY_KEYS = [
  "companyName", "identificationShortName", "companyMembershipLevel", "companyPhoneCode", "companyPhoneNo", "companyEmail",
  "companyAddress", "postalCode", "companyCountryCode", "companyState", "companyCity", "defaultCurrency", "openingCashBalance",
  "openingDueBalance", "openingBalanceRemarks",
];
const CUSTOMER_USER_KEYS = ["title", "firstname", "lastname", "phoneCode", "phoneNo", "email"];
export const CUSTOMER_STATUS_KEYS = ["isActive", "creditEligibility", "isCreditRequestEligible", "isPartialPaymentEligible", "isCreditAutoSettle", "isPartialAutoSettle", "showVendorOnFlightSearch"];

const CUSTOMER_SEED = [
  ["Sky Travels", "SKT", "Gold", "BD", "Dhaka", "Dhaka", "APPROVED", true, 185000, 25000, 5000, "Mr.", "Karim", "Ahmed"],
  ["Green Trade", "GTR", "Silver", "BD", "Chattogram", "Chattogram", "APPROVED", true, 72000, 0, 0, "Mrs.", "Nusrat", "Jahan"],
  ["Blue Wave Ltd", "BWL", "Platinum", "BD", "Dhaka", "Gazipur", "REQUESTED", false, 0, 0, 0, "Mr.", "Rafiq", "Islam"],
  ["Alpha Traders", "ALT", "Bronze", "AE", "Dubai", "Dubai", "REJECTED", false, 0, 0, 0, "Mrs.", "Tania", "Akter"],
  ["Delta Corp", "DLC", "Corporate", "BD", "Sylhet", "Sylhet", "APPROVED", true, 410000, 60000, 12000, "Mr.", "Imran", "Hossain"],
  ["Omega Exports", "OMX", "Diamond", "BD", "Rajshahi", "Rajshahi", "DELETED", false, 0, 0, 0, "Mrs.", "Farhana", "Yeasmin"],
].map(([companyName, code, level, country, state, city, approvalStatus, isActive, cash, limit, due, title, firstname, lastname], i) => ({
  id: i + 1,
  identificationShortCode: `AG-${1001 + i}`,
  identificationShortName: code,
  companyName,
  companyMembershipLevel: level,
  companyPhoneCode: "+880",
  companyPhoneNo: String(1711000300 + i * 19),
  companyEmail: `info@${companyName.toLowerCase().replace(/[^a-z]/g, "")}.com`,
  companyAddress: `${20 + i} Commercial Area`,
  postalCode: String(1200 + i),
  companyCountryCode: country,
  companyState: state,
  companyCity: city,
  defaultCurrency: "BDT",
  approvalStatus,
  isActive,
  zone: { id: (i % 3) + 1, name: ["Dhaka", "Chattogram", "Sylhet"][i % 3] },
  tradeLicenseNo: `TL-${50000 + i * 31}`,
  nidScanCopyUrl: i % 2 === 0 ? "https://example.com/nid.pdf" : null,
  tradeLicenseScanUrl: i % 3 === 0 ? "https://example.com/trade-license.pdf" : null,
  createdDateTime: dayjs().subtract(60 - i * 7, "day").toISOString(),
  b2bAgencyUser: [{ user: { title, firstname, lastname, email: `${firstname.toLowerCase()}@example.com`, phoneCode: "+880", phoneNo: String(1811000300 + i * 13) } }],
  b2bUserWallet: {
    walletId: i + 1,
    wallet: {
      id: i + 1,
      walletCurrency: "BDT",
      cashBalance: cash,
      creditBalance: limit,
      creditDueAmount: due,
      openingCashBalance: 0,
      openingDueBalance: 0,
      creditEligibility: limit > 0,
      isCreditRequestEligible: limit > 0,
      isPartialPaymentEligible: i % 2 === 0,
      creditDueTimeLimit: limit > 0 ? 15 : 0,
    },
  },
}));

// tm AgentAccountLedger: user-account-ledger/findByB2bUserId?b2bUserId=...
const customerLedgerSeed = () =>
  CUSTOMER_SEED.filter((c) => c.approvalStatus === "APPROVED").flatMap((customer) =>
    [
      ["Deposit", "DEPOSIT", 0, 50000, ""],
      ["Booking", "PAYMENT", 12500, 0, "FLIGHT"],
      ["Refund", "REFUND", 0, 3000, "FLIGHT"],
    ].map(([transactionType, transactionCategory, debit, credit, service], n) => ({
      b2bUserId: customer.id,
      createdDateTime: dayjs().subtract((3 - n) * 4, "day").toISOString(),
      referenceId: `AL-${customer.id}${n + 1}0${n + 3}`,
      transactionCategory,
      transactionType,
      service,
      narration: `${transactionType} entry`,
      debit,
      credit,
      currentBalance: 50000 - (n > 0 ? 12500 : 0) + (n === 2 ? 3000 : 0),
      creditBalance: customer.b2bUserWallet.wallet.creditBalance,
      creditUsed: 0,
      creditDueAmount: customer.b2bUserWallet.wallet.creditDueAmount,
    }))
  );

const customerSectionLabel = (text) => section(text);

// list = users/all-agent; one row = users/find-agent/:id, create-agent, update-agent/:id, remove-agent/:id
define("customer", "Customer", "users/all-agent", {
  writeEndpoint: "users",
  getEndpoint: "users/find-agent",
  createEndpoint: "users/create-agent",
  updateEndpoint: "users/update-agent",
  deleteEndpoint: "users/remove-agent",
  detailPath: "/dashboard/customer-details",
  walletPath: "/dashboard/customer-wallet",
  walletSummary: (r) => ({
    title: [r.identificationShortCode, r.companyName].filter(Boolean).join(" - "),
    pairs: [
      ["Wallet Balance", walletMoney(r.b2bUserWallet?.wallet?.cashBalance, r.b2bUserWallet?.wallet?.walletCurrency || "BDT")],
      ["Credit Limit", walletMoney(r.b2bUserWallet?.wallet?.creditBalance, "BDT")],
      ["Credit Due", walletMoney(r.b2bUserWallet?.wallet?.creditDueAmount, "BDT")],
    ],
  }),
  addLabel: "Add Customer",
  saveMessage: "Customer",
  seed: CUSTOMER_SEED,
  // tm tabs: ALL / Approved / Requested / Rejected / Deleted / Inactive (Inactive = status filter, the rest approvalStatus)
  tabs: {
    param: "approvalStatus",
    paramsFor: (tab) => (tab === "INACTIVE" ? { status: "Inactive" } : { approvalStatus: tab }),
    items: [
      { label: "ALL", value: "ALL" },
      { label: "Approved", value: "APPROVED" },
      { label: "Requested", value: "REQUESTED" },
      { label: "Rejected", value: "REJECTED" },
      { label: "Deleted", value: "DELETED" },
      { label: "Inactive", value: "INACTIVE" },
    ],
  },
  actionIcons: [{ label: "Transaction Ledger", to: (row) => `/dashboard/customer-account-ledger-list?b2bUserId=${row.id}` }],
  filters: [
    { name: "companyMembershipLevel", label: "Category", options: MEMBERSHIP_LEVELS },
    { name: "zoneId", label: "Zone", optionsFrom: "zone-lookup" },
    { name: "status", label: "Status", options: ["Active", "Inactive"] },
    { name: "companyCountryCode", label: "Country Code", options: ["BD", "AE", "US", "CA", "GB", "AU"] },
  ],
  // mock mode only: the real API applies these query params itself
  mockFilters: {
    approvalStatus: (row, value) => value === "ALL" || row.approvalStatus === value,
    status: (row, value) => (value === "Inactive" ? !row.isActive : value === "Active" ? Boolean(row.isActive) : true),
    zoneId: (row, value) => String(row.zone?.id) === String(value),
  },
  columns: [
    col("identificationShortCode", "Customer ID", "text", { link: (row) => `/dashboard/customer-details/${row.id}` }),
    col("companyName", "Agency Name"),
    col("zone", "Zone", "text", { value: (r) => r.zone?.name || "N/A" }),
    col("companyMembershipLevel", "Category"),
    col("companyPhoneNo", "Phone No", "text", { value: (r) => agencyPhone(r) }),
    col("companyEmail", "Email"),
    col("walletBalance", "Wallet Balance", "amount", { value: (r) => agentWallet(r).cashBalance || 0 }),
    col("isActive", "Active", "status"),
    col("approvalStatus", "Status", "status"),
  ],
  details: [
    col("createdDateTime", "Registered At", "datetime"),
    col("kam", "KAM", "text", { value: (r) => [r.zone?.KAMUser?.identificationShortCode, [r.zone?.KAMUser?.firstname, r.zone?.KAMUser?.lastname].filter(Boolean).join(" ")].filter(Boolean).join(" - ") || "N/A" }),
    col("zoneDistrict", "Zone District", "text", { value: (r) => r.zone?.district || "N/A" }),
  ],
  actions: {
    edit: "Edit Agent",
    delete: "Delete",
    extra: [
      { label: "View Profile", to: (row) => `/dashboard/customer-details/${row.id}` },
      { label: "Deposit Request", to: (row) => `/dashboard/deposit-request-list?b2bUserId=${row.id}` },
      { label: "Credit Request", to: (row) => `/dashboard/credit-request-list?b2bUserId=${row.id}` },
    ],
  },
  detailView: [
    {
      title: "Agency Information",
      items: [
        col("identificationShortCode", "Customer ID"),
        col("companyName", "Agency / Company"),
        col("companyMembershipLevel", "Membership Category"),
        col("approvalStatus", "Approval Status", "status"),
        col("isActive", "Active", "status"),
        col("zone", "Zone", "text", { value: (r) => r.zone?.name || "N/A" }),
        col("companyPhoneNo", "Business Phone", "text", { value: (r) => agencyPhone(r) }),
        col("companyEmail", "Business E-mail"),
        col("companyAddress", "Address"),
        col("companyCountryCode", "Country"),
        col("companyState", "State / Division"),
        col("companyCity", "District"),
        col("postalCode", "Postal Code"),
        col("defaultCurrency", "Currency"),
      ],
    },
    {
      title: "Primary User",
      items: [
        col("userName", "Name", "text", { value: (r) => [agentUser(r).title, agentUser(r).firstname, agentUser(r).lastname].filter(Boolean).join(" ") || "N/A" }),
        col("userPhone", "Phone No", "text", { value: (r) => [agentUser(r).phoneCode, agentUser(r).phoneNo].filter(Boolean).join("") || "N/A" }),
        col("userEmail", "Email", "text", { value: (r) => agentUser(r).email || "N/A" }),
      ],
    },
    {
      title: "Wallet",
      items: [
        col("cash", "Wallet Balance", "amount", { value: (r) => agentWallet(r).cashBalance || 0 }),
        col("limit", "Credit Limit", "amount", { value: (r) => agentWallet(r).creditBalance || 0 }),
        col("due", "Credit Due", "amount", { value: (r) => agentWallet(r).creditDueAmount || 0 }),
        col("openCash", "Opening Balance", "amount", { value: (r) => agentWallet(r).openingCashBalance || 0 }),
        col("openDue", "Opening Due", "amount", { value: (r) => agentWallet(r).openingDueBalance || 0 }),
        col("creditEligibility", "Credit Eligible", "yesno", { value: (r) => Boolean(agentWallet(r).creditEligibility) }),
        col("isPartialPaymentEligible", "Partial Payment Eligible", "yesno", { value: (r) => Boolean(agentWallet(r).isPartialPaymentEligible) }),
        col("creditDueTimeLimit", "Credit Due Limit (Days)", "text", { value: (r) => agentWallet(r).creditDueTimeLimit ?? 0 }),
      ],
    },
    {
      title: "Documents",
      items: [
        col("tradeLicenseScanUrl", "Trade License", "link", { linkLabel: "View" }),
        col("nidScanCopyUrl", "NID", "link", { linkLabel: "View" }),
      ],
    },
    {
      title: "Created & Updated Users",
      items: [CREATED_BY, CREATED_AT, col("updatedDateTime", "Updated Date Time", "datetime")],
    },
  ],
  // The Agency + Primary User steps go in one create-agent / update-agent call; the Status step is
  // patched after it (see afterSave). tm leaves out short name / email / phone when they did not change.
  prepare: (payload, existing) => {
    const body = Object.fromEntries([...CUSTOMER_AGENCY_KEYS, ...CUSTOMER_USER_KEYS].filter((key) => payload[key] !== undefined).map((key) => [key, payload[key]]));
    ["openingCashBalance", "openingDueBalance"].forEach((key) => {
      if (body[key] !== undefined && body[key] !== "") body[key] = Number(body[key]);
    });
    if (existing) {
      const user = agentUser(existing);
      if (body.identificationShortName === existing.identificationShortName) delete body.identificationShortName;
      if (body.email === user.email) delete body.email;
      if (body.phoneCode === user.phoneCode) delete body.phoneCode;
      if (body.phoneNo === user.phoneNo) delete body.phoneNo;
    }
    return body;
  },
  fields: [
    customerSectionLabel("Agency/Company Basic Information"),
    f("companyName", "Agency / Company Name", "text", { required: true, placeholder: "Agency or Company Name" }),
    f("identificationShortName", "Agent Short Code", "text", { required: true, placeholder: "3 Digit Code [A-Z]" }),
    f("companyMembershipLevel", "Membership Category", "select", { required: true, options: MEMBERSHIP_LEVELS, placeholder: "Select Membership Category" }),
    f("companyPhoneCode", "Business Phone Code", "phonecode", { required: true }),
    f("companyPhoneNo", "Business Phone Number", "text", { required: true, placeholder: "Phone Number" }),
    f("companyEmail", "Business E-mail", "email", { required: true, placeholder: "E-mail" }),
    f("companyAddress", "Agency/Company Address", "text", { placeholder: "Vendor Full Address" }),
    f("postalCode", "Postal Code", "text", { placeholder: "Postal Code" }),
    f("companyCountryCode", "Country", "select", {
      required: true,
      placeholder: "Select Country",
      options: GEO.map((c) => ({ value: c.alpha2Code, label: c.name })),
      resets: ["companyState", "companyCity"],
    }),
    f("companyState", "State / Division", "select", {
      required: true,
      placeholder: "Select State / Division",
      options: (v) => (geoCountry(v)?.states || []).map((s) => s.name),
      resets: ["companyCity"],
    }),
    f("companyCity", "District", "select", {
      required: true,
      placeholder: "Select District",
      options: (v) => geoState(v)?.districts || [],
    }),
    f("defaultCurrency", "Currency", "select", {
      required: true,
      default: "BDT",
      options: [...new Set(countries.map((c) => c.currency).filter(Boolean))],
      placeholder: "Select Currency",
      prefill: (e) => e.defaultCurrency || e.b2bUserWallet?.wallet?.walletCurrency,
    }),
    f("openingCashBalance", "Opening Wallet Balance", "number", { required: true, default: 0, placeholder: "Enter wallet Balance", prefill: (e) => agentWallet(e).openingCashBalance ?? e.openingCashBalance }),
    f("openingDueBalance", "Opening Credit Balance", "number", { required: true, default: 0, placeholder: "Enter Credit Balance", prefill: (e) => agentWallet(e).openingDueBalance ?? e.openingDueBalance }),
    f("openingBalanceRemarks", "Opening Balance Remarks", "text", { placeholder: "Enter Opening Balance Remarks" }),
    f("logoFile", "Upload Logo", "file"),
    customerSectionLabel("Primary User Information"),
    f("title", "Select Title", "select", { required: true, options: ["Mr.", "Mrs."], placeholder: "Select Title", prefill: (e) => agentUser(e).title }),
    f("firstname", "First Name", "text", { required: true, placeholder: "First Name", prefill: (e) => agentUser(e).firstname }),
    f("lastname", "Last Name", "text", { required: true, placeholder: "Last Name", prefill: (e) => agentUser(e).lastname }),
    f("phoneCode", "Phone Code", "phonecode", { required: true, prefill: (e) => agentUser(e).phoneCode }),
    f("phoneNo", "Phone Number", "text", { required: true, placeholder: "Phone Number", prefill: (e) => agentUser(e).phoneNo }),
    f("email", "Email", "email", { required: true, placeholder: "Email", prefill: (e) => agentUser(e).email }),
    customerSectionLabel("Required Documents"),
    f("tradeLicenseFile", "Trade License (Max size 1MB. Allowed only PNG or JPEG or PDF)", "file"),
    f("nidFile", "NID", "file"),
    customerSectionLabel("Agent Activation Information"),
    f("isActive", "Active Status", "status", { required: true, default: true }),
    f("isPartialPaymentEligible", "Partial Payment Eligible", "select", { options: YES_NO, placeholder: "Select Option", prefill: (e) => agentWallet(e).isPartialPaymentEligible }),
    f("creditEligibility", "Credit Eligible", "select", { options: YES_NO, placeholder: "Select Option", prefill: (e) => agentWallet(e).creditEligibility }),
    f("isCreditRequestEligible", "Credit Request Eligible", "select", { options: YES_NO, placeholder: "Select Option", prefill: (e) => agentWallet(e).isCreditRequestEligible }),
    f("creditDueTimeLimit", "Credit Due Limit (Days)", "number", { placeholder: "Enter Days", showIf: (v) => v.isCreditRequestEligible === true, prefill: (e) => agentWallet(e).creditDueTimeLimit }),
    f("isPartialAutoSettle", "Partial Auto Settle", "select", { options: YES_NO, placeholder: "Select Option" }),
    f("isCreditAutoSettle", "Credit Auto Settle", "select", { options: YES_NO, placeholder: "Select Option" }),
    f("showVendorOnFlightSearch", "Show Vendor", "select", { options: YES_NO, placeholder: "Select Option" }),
    f("zoneArea", "Zone / Area", "relation", { required: true, optionsFrom: "zone-lookup", placeholder: "Select Zone", prefill: (e) => e.zone?.id }),
  ],
  uploads: [
    { field: "logoFile", path: "upload-company-logo" },
    { field: "tradeLicenseFile", path: "upload-trade-license" },
    { field: "nidFile", path: "upload-nid-copy" },
  ],
  optionLabel: (r) => `${r.identificationShortCode || ""} - ${r.companyName || ""}`,
});

// tm AgentAccountLedger: user-account-ledger/findByB2bUserId?b2bUserId=...
define("customer-account-ledger", "Customer Account Ledger", "user-account-ledger/findByB2bUserId", {
  writeEndpoint: "user-account-ledger",
  listPath: "/dashboard/customer-account-ledger-list",
  reconcile: { scopeKey: "b2bUserId", noun: "customer ledger", path: (id) => `user-balance/agent/reconciliation/${id}` },
  banner: "customer", // customer name / id strip above the ledger (query: ?b2bUserId=)
  bannerParam: "b2bUserId",
  ledger: { balanceBase: "user-account-ledger", updateBase: "user-account-ledger", editTransactionLabel: "Edit Trx Date", uploadPath: "upload-scan-copy" },
  actions: { delete: "Delete" },
  summary: [SUMMARY_BALANCE],
  readOnly: true,
  seed: customerLedgerSeed(),
  pageSizes: [20, 50, 100, 200, 500],
  dateFilter: "createdDateTime",
  columns: [
    col("createdDateTime", "Transation Date", "datetime"),
    col("referenceId", "Reference ID"),
    col("transactionType", "Type", "text", { options: ["Deposit", "Booking", "Refund"] }),
    col("service", "Service", "text", { options: SERVICES }),
    col("narration", "Narration"),
    col("debit", "Debit [DR]", "amount"),
    col("credit", "Credit [CR]", "amount"),
    col("currentBalance", "Cash Balance", "amount"),
    col("creditBalance", "Credit Limit", "amount"),
    col("creditUsed", "Credit Used", "amount"),
    col("creditDueAmount", "Credit Due", "amount"),
  ],
});

/* ------------------------------- Customer Carts ------------------------------- */
// Read-only view of what customers have in their cart (dm-commerce keeps carts in the database;
// adminbeapigateway GET carts / carts/:id). Search = customer name / code / email / phone / company,
// the date range filters on the last update.

const cartCustomer = (r) => [r.customerCode, r.customerName].filter(Boolean).join(" - ") || "N/A";
const bdtText = (n) => `${(+n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} BDT`;
const mockCartItem = (id, title, source, variations) => ({
  id,
  productCode: `${source}-${900000 + id}`,
  title,
  source,
  note: "",
  variations: variations.map(([skuId, variant, unitPrice, quantity]) => ({ skuId, variant, unitPrice, quantity, total: unitPrice * quantity })),
});
const mockCart = (id, user, company, items, hoursAgo) => {
  const lines = items.flatMap((item) => item.variations);
  return {
    id,
    customerName: user[0],
    customerCode: user[1],
    email: user[2],
    phone: user[3],
    companyName: company[0],
    companyCode: company[1],
    items: items.map((item) => ({ ...item, quantity: item.variations.reduce((s, v) => s + v.quantity, 0), total: item.variations.reduce((s, v) => s + v.total, 0) })),
    itemCount: items.length,
    totalQuantity: lines.reduce((s, v) => s + v.quantity, 0),
    cartTotal: lines.reduce((s, v) => s + v.total, 0),
    createdDateTime: new Date(Date.now() - (hoursAgo + 30) * 3600000).toISOString(),
    updatedDateTime: new Date(Date.now() - hoursAgo * 3600000).toISOString(),
  };
};

define("customer-cart", "Customer Carts", "carts", {
  readOnly: true,
  detailPath: "/dashboard/customer-cart-details",
  dateFilter: "updatedDateTime",
  // the arrow of a row opens the products of the cart (image, variations, price x quantity, totals)
  expandComponent: CartItemsPanel,
  seed: [
    mockCart(1, ["Karim Ahmed", "TM1021", "karim@example.com", "+8801711000001"], ["Sky Travels", "AG-1002"], [mockCartItem(1, "Wireless Earbuds Pro", "osee", [["sku-1", { Color: "Black" }, 850, 4], ["sku-2", { Color: "White" }, 850, 2]]), mockCartItem(2, "Cotton Panjabi", "osee", [["sku-3", { Size: "L" }, 1200, 3]])], 2),
    mockCart(2, ["Nusrat Jahan", "TM1022", "nusrat@example.com", "+8801711000002"], ["Green Trade", "AG-1003"], [mockCartItem(3, "Smart Watch Series 5", "osee", [["sku-4", { Color: "Silver" }, 2300, 3]])], 20),
    mockCart(3, ["Rafiq Islam", "TM1023", "rafiq@example.com", "+8801711000003"], ["Blue Wave Ltd", "AG-1004"], [mockCartItem(4, "Leather Wallet", "1688", [["sku-5", { Color: "Brown" }, 540, 10]]), mockCartItem(5, "Table Lamp", "1688", [["sku-6", { Style: "Round" }, 760, 5], ["sku-7", { Style: "Square" }, 790, 5]])], 52),
    mockCart(4, ["Tania Akter", "TM1024", "tania@example.com", "+8801711000004"], ["Alpha Traders", "AG-1005"], [mockCartItem(6, "Running Shoes", "osee", [["sku-8", { Size: "42" }, 1450, 3]])], 130),
  ],
  columns: [
    col("customerName", "Customer", "text", { value: cartCustomer, link: (row) => `/dashboard/customer-cart-details/${row.id}` }),
    col("companyName", "Company", "text", { value: (r) => [r.companyCode, r.companyName].filter(Boolean).join(" - ") || "N/A" }),
    // the first products by name, so the list already tells what is in the cart (all of them under the row arrow)
    col("items", "Products", "text", {
      value: (r) => {
        const titles = (r.items || []).map((item) => item.title).filter(Boolean);
        if (titles.length === 0) return "-";
        return titles.length > 2 ? `${titles.slice(0, 2).join(", ")} +${titles.length - 2} more` : titles.join(", ");
      },
    }),
    col("itemCount", "Items In Cart", "number"),
    col("totalQuantity", "Total Quantity", "number"),
    col("cartTotal", "Cart Total", "text", { value: (r) => bdtText(r.cartTotal) }),
    col("updatedDateTime", "Last Updated", "datetime"),
  ],
  detailView: [
    {
      title: "Customer",
      items: [
        col("customerName", "Customer", "text", { value: cartCustomer }),
        col("email", "Email"),
        col("phone", "Phone"),
        col("companyName", "Company", "text", { value: (r) => [r.companyCode, r.companyName].filter(Boolean).join(" - ") || "N/A" }),
      ],
    },
    {
      title: "Cart Summary",
      items: [
        col("itemCount", "Items In Cart"),
        col("totalQuantity", "Total Quantity"),
        col("cartTotal", "Cart Total", "text", { value: (r) => bdtText(r.cartTotal) }),
        col("createdDateTime", "Created", "datetime"),
        col("updatedDateTime", "Last Updated", "datetime"),
      ],
    },
    {
      title: "Cart Items",
      component: CartItemsPanel,
    },
  ],
});

/* ------------------------------- Communication ------------------------------- */

define("sms-template", "SMS Template", "sms-templates", {
  formPath: "/dashboard/smstemplate",
  listParams: { type: "Sms" },
  submitExtras: { type: "Sms" },
  clientFilter: (row) => !row.type || row.type === "Sms",
  columns: [col("title", "Title"), col("description", "Description")],
  actions: { edit: "Edit SMS", delete: "Delete SMS" },
  fields: [
    f("title", "Title", "text", { required: true, placeholder: "Sms Title" }),
    f("description", "Message", "textarea", { required: true, placeholder: "Message" }),
  ],
  optionLabel: (r) => r.title,
  seed: [
    { title: "Booking Confirmed", description: "Your booking is confirmed. Thank you for choosing Deshmart." },
    { title: "Payment Received", description: "We have received your payment. Your order is being processed." },
    { title: "Order Shipped", description: "Your order has been shipped and is on its way." },
    { title: "OTP Message", description: "Your verification code is {{code}}. It expires in 5 minutes." },
  ],
});

define("email-template", "Email Template", "sms-templates", {
  formPath: "/dashboard/emailtemplate",
  listParams: { type: "Email" },
  submitExtras: { type: "Email" },
  clientFilter: (row) => !row.type || row.type === "Email",
  columns: [col("title", "Title"), col("description", "Description", "html")],
  actions: {
    edit: "Edit Email",
    delete: "Delete Email",
    view: { label: "Preview", title: (row) => row.title, html: (row) => row.description },
    download: [{ label: "Download", keys: ["attachment", "attachmentUrl"] }],
  },
  fields: [
    f("title", "Title", "text", { required: true, placeholder: "Email Title" }),
    f("attachmentFile", "Attachment", "file"),
    f("description", "Message", "richtext", { required: true }),
  ],
  uploads: [{ field: "attachmentFile", path: "upload-attachment" }],
  optionLabel: (r) => r.title,
  seed: [
    { title: "Welcome", description: "<p>Hello,</p><p>Welcome to <b>Deshmart</b>. Your account is ready.</p>" },
    { title: "Invoice", description: "<p>Hello,</p><p>Please find your invoice attached.</p>" },
    { title: "Order Update", description: "<p>Hello,</p><p>There is an update on your order.</p>" },
  ],
});

// tm: /dashboard/sms-list and /dashboard/email-list (history of sent messages, linked from the send pages)
define("sms-history", "Sms List", "sms", {
  readOnly: true,
  listPath: "/dashboard/sms-list",
  columns: [col("title", "Title"), col("message", "Message")],
  actions: { view: { label: "View", title: (row) => row.title, pairs: (row) => [["Title", row.title], ["Message", row.message]] } },
});

define("email-history", "Email List", "mail", {
  readOnly: true,
  listPath: "/dashboard/email-list",
  columns: [col("title", "Title")],
  actions: { view: { label: "View", title: (row) => row.title, pairs: (row) => [["Title", row.title]], html: (row) => row.message } },
});

